/**
 * SIJO AI Sleep Advisor — chat client (Build Spec v1.8).
 *
 * Two surfaces share this file (one engine, one session):
 * - Dedicated page (sections/ai-sleep-advisor.liquid): [data-autoboot].
 * - Banner popup drawer (sections/advisor-banner.liquid): boots lazily the
 *   first time the drawer opens, so browsing never creates advisor sessions.
 *
 * Sessions are surface-agnostic: the transcript + session id live in
 * sessionStorage for the visit, so a conversation started in the drawer
 * persists across page navigation and resumes on the dedicated page (and
 * vice versa). The server holds all field state; the client only replays
 * the visible transcript.
 */
(function () {
  var TOKEN_KEY = 'sijo_advisor_token';
  var SIZE_LABELS = { twin: 'Twin', twin_xl: 'Twin XL', full: 'Full', queen: 'Queen', king: 'King', cal_king: 'Cal King' };

  // Grouped variant sizes ("Full / Queen", "Standard / Queen") match on any
  // slash-separated part, so bed_size=queen lands on both.
  function sizeMatches(value, want) {
    if (!want) return false;
    if (value === want) return true;
    return value.split('/').some(function (part) { return part.trim() === want; });
  }

  var STATE_KEY = 'sijo_advisor_state';
  var SID_KEY = 'sijo_advisor_sid';
  var KLAVIYO_COMPANY_ID = 'Qpbsx2'; // back-in-stock capture (Klaviyo client API)
  // Visit-scoped, but in localStorage: sessionStorage is per-tab, and customers
  // open product pages in new tabs while deciding — the conversation and the
  // recommendation must follow them there. The TTL keeps "visit" semantics.
  var STATE_TTL_MS = 12 * 60 * 60 * 1000;

  function loadState() {
    try {
      var raw = localStorage.getItem(STATE_KEY) || sessionStorage.getItem(STATE_KEY);
      if (!raw) return null;
      var state = JSON.parse(raw);
      if (state.saved_at && Date.now() - state.saved_at > STATE_TTL_MS) {
        localStorage.removeItem(STATE_KEY);
        return null;
      }
      return state;
    } catch (e) {
      return null;
    }
  }

  function saveState(state) {
    try {
      state.saved_at = Date.now();
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
      if (state.id) localStorage.setItem(SID_KEY, state.id);
    } catch (e) { /* storage full/blocked — session continues without persistence */ }
  }

  function clearState() {
    try { localStorage.removeItem(STATE_KEY); localStorage.removeItem(SID_KEY); } catch (e) {}
    try { sessionStorage.removeItem(STATE_KEY); } catch (e) {}
  }

  function initAdvisor(root) {
    if (root.dataset.advisorBooted) return;
    root.dataset.advisorBooted = 'true';

    var API = (root.dataset.apiBase || '').replace(/\/$/, '');
    var thread = root.querySelector('[data-advisor-thread]');
    // In the drawer the scrollable element is the drawer body, not the thread.
    var scroller = root.closest('.advisor-drawer__body') || thread;
    function scrollToEnd() {
      requestAnimationFrame(function () { scroller.scrollTop = scroller.scrollHeight; });
    }
    var chipsEl = root.querySelector('[data-advisor-chips]');
    var composer = root.querySelector('[data-advisor-composer]');
    var input = composer.querySelector('input');
    var optinTemplate = root.querySelector('[data-advisor-optin-template]');
    var imageMap = {};
    try {
      var imgEl = root.querySelector('[data-advisor-images]');
      if (imgEl) imageMap = JSON.parse(imgEl.textContent);
    } catch (e) { /* photo chips degrade to text */ }

    var sessionId = null;
    var busy = false;
    var state = { id: null, msgs: [], chips: [], rec: null, done: false };
    var resultsEl = null; // the results-moment container (swapped whole on an updated rec)
    var scrollTargetRole = null; // post-invite: land on the new pillow card, not the system top
    var pendingCsat = null; // calibration-sample rating, deferred to the customer's final action

    // csat.ask (revised July 22): one ask at a time. The rating renders only
    // after the prior ask is resolved or the customer takes a final action
    // (invite declined, add-to-cart) — never at the results reveal, never
    // stacked. Guarded by the per-session local flag; the server samples.
    function renderCsatNow() {
      if (!pendingCsat || !sessionId) return;
      if (localStorage.getItem('advisor:csat:' + sessionId)) { pendingCsat = null; return; }
      // Another ask still open (e.g. the pillow or upgrade invite)? Wait —
      // its decline handler re-invokes this once that ask resolves.
      var askHost = resultsEl && resultsEl.isConnected ? resultsEl : thread;
      if (askHost.querySelector('.sleep-advisor__chips--inline')) return;
      if (root.querySelector('.sleep-advisor__csat')) return;
      var text = pendingCsat;
      pendingCsat = null;
      // Placement (revised July 22): below the save-your-system opt-in and
      // above Start over, in the QUESTION typography — never paragraph style
      // (found case: the plain-paragraph ask read as a seam), never at the
      // results reveal, never stacked with another ask.
      var block = document.createElement('div');
      block.className = 'sleep-advisor__csat';
      var csatMsg = document.createElement('div');
      csatMsg.className = 'sleep-advisor__msg sleep-advisor__msg--advisor sleep-advisor__msg--question';
      csatMsg.textContent = text;
      block.appendChild(csatMsg);
      var csatRow = document.createElement('div');
      csatRow.className = 'sleep-advisor__chips sleep-advisor__chips--inline';
      for (var ci = 1; ci <= 5; ci++) {
        (function (score) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'sleep-advisor__chip';
          btn.textContent = score + (score === 1 ? ', rough' : score === 5 ? ', great' : '');
          btn.addEventListener('click', function () {
            try { localStorage.setItem('advisor:csat:' + sessionId, String(score)); } catch (e) { /* private mode */ }
            csatRow.remove();
            csatMsg.textContent = 'Thank you. That helps me get better.';
            api('/v1/session/' + sessionId + '/csat', { score: score }).catch(function () {});
          });
          csatRow.appendChild(btn);
        })(ci);
      }
      block.appendChild(csatRow);
      var restartEl = root.querySelector('.sleep-advisor__restart');
      if (restartEl) root.insertBefore(block, restartEl);
      else root.appendChild(block);
    }

    if (!API) {
      addMessage('advisor', 'The advisor is almost ready. Check back soon.', false);
      composer.style.display = 'none';
      return;
    }

    function persist() {
      state.id = sessionId;
      saveState(state);
    }

    function entryPoint() {
      var params = new URLSearchParams(window.location.search);
      if (params.get('checkin')) return 'checkin';
      var ref = params.get('from');
      if (ref && /^(pdp|collection):[a-z0-9-]+$/.test(ref)) return ref;
      var base = root.dataset.entryPoint || 'page:direct';
      if (base === 'page:direct') return base;
      // v4.1 placement-aware entries: <placement>:<page_type>[:<handle>] —
      // which surface opened the drawer (each surface stamps
      // window.__islaPlacement before opening; a trusted click on the shared
      // trigger stamps banner/band) plus the specific page it happened on.
      // The compare flag also keeps its greeting frame (isla.greeting).
      var placement = window.__islaEntrySource === 'compare' ? 'compare'
        : (window.__islaPlacement || 'banner');
      var pt = (base.split(':')[1] || 'direct').replace(/[^a-z0-9_-]/g, '-');
      var m = location.pathname.match(/\/(collections|pages|products)\/([a-z0-9-]+)/);
      var handle = m ? m[2]
        : (location.pathname.match(/\/blogs\/[a-z0-9-]+\/([a-z0-9-]+)/) || [])[1];
      return placement + ':' + pt + (handle ? ':' + handle : '');
    }

    function api(path, body) {
      return fetch(API + path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body || {})
      }).then(function (res) {
        if (!res.ok) {
          // Carry the status and the server's error code up to the caller, so a
          // service outage can be told apart from a one-off failed request.
          return res.json().catch(function () { return {}; }).then(function (b) {
            var err = new Error(b.error || ('advisor api ' + res.status));
            err.status = res.status;
            err.code = b.code || '';
            throw err;
          });
        }
        return res.json();
      });
    }

    // A 503 means the advisor service itself is down, and those outages run in
    // hours, not seconds (Aug 31 2026: the database sat past its compute quota
    // for most of a day). "Try again" and "refresh in a moment" just loop the
    // customer through the same failure, so an outage gets its own honest
    // message and closes the inputs instead.
    var offline = false;
    function isOutage(e) {
      return !!e && (e.code === 'db_unavailable' || e.status === 503);
    }
    function showOutage() {
      if (offline) return;
      offline = true;
      chipsEl.innerHTML = '';
      composer.style.display = 'none';
      addMessage('advisor', 'The sleep advisor is offline for maintenance right now. Everything else on the site is working normally \u2014 please check back a little later.', false);
    }

    function addMessage(role, text, save, link) {
      var el = document.createElement('div');
      el.className = 'sleep-advisor__msg sleep-advisor__msg--' + role;
      el.textContent = text;
      // Owner (Aug 6): the collection link lives ON the fabric name in the
      // reveal line — not a separate browse line below the card, which was
      // easy to miss and lost on restore. Saved with the message so a
      // reopened drawer keeps it.
      if (link) {
        var m = text.match(/^(Your fabric match:\s*)([^.]+)(\.)/);
        if (m) {
          el.textContent = '';
          el.appendChild(document.createTextNode(m[1]));
          var a = document.createElement('a');
          a.href = link;
          a.textContent = m[2];
          el.appendChild(a);
          el.appendChild(document.createTextNode(m[3] + text.slice(m[0].length)));
        }
      }
      thread.appendChild(el);
      scrollToEnd();
      if (save !== false) {
        state.msgs.push(link ? { role: role, text: text, link: link } : { role: role, text: text });
        persist();
      }
      return el;
    }

    function addTyping() {
      var el = addMessage('advisor', '…', false);
      el.classList.add('sleep-advisor__msg--typing');
      el.setAttribute('aria-hidden', 'true');
      return el;
    }

    function renderChips(chips, save) {
      chipsEl.innerHTML = '';
      // A question with no chips is a free-text prompt — the composer is the
      // answer box, so its placeholder should read like one.
      var hasChips = !!(chips && chips.length);
      input.placeholder = hasChips ? 'Or just tell me in your own words…' : 'Type your answer…';
      if (!hasChips && composer.style.display !== 'none') input.focus();
      var hasPhotos = (chips || []).some(function (c) { return c.image && imageMap[c.image]; });
      chipsEl.classList.toggle('sleep-advisor__chips--photos', hasPhotos);
      (chips || []).forEach(function (chip) {
        var btn = document.createElement('button');
        btn.type = 'button';
        var photoUrl = chip.image && imageMap[chip.image];
        if (photoUrl) {
          btn.className = 'sleep-advisor__chip sleep-advisor__chip--photo';
          var img = document.createElement('img');
          img.src = photoUrl;
          img.alt = '';
          img.loading = 'lazy';
          btn.appendChild(img);
          var label = document.createElement('span');
          label.textContent = chip.label;
          btn.appendChild(label);
        } else {
          btn.className = 'sleep-advisor__chip';
          btn.textContent = chip.label;
        }
        btn.addEventListener('click', function () {
          if (busy) return;
          addMessage('customer', chip.label);
          send({ chip: { field: chip.field, value: chip.value } });
        });
        chipsEl.appendChild(btn);
      });
      if (chips && chips.length) scrollToEnd();
      if (save !== false) {
        state.chips = chips || [];
        persist();
      }
    }

    // v3.8 support handoff: off-scope questions go to Gorgias. The drawer
    // minimizes with the session fully preserved (localStorage state), and
    // the widget opens with the customer's own question pre-filled where the
    // installed v3 widget supports it; where it doesn't, the order-status
    // line points at the native Track-orders affordance instead. Only the
    // question text ever passes over — never the sleep profile.
    // Build-time check resolved (July 22, live probe): the installed Gorgias
    // v3 widget exposes setCurrentInput(text) — a true draft prefill. The
    // question lands in the input for the customer to review and send
    // themselves (never auto-sent; sendMessage exists but is deliberately
    // not used).
    function gorgiasPrefillSupported() {
      var g = window.GorgiasChat;
      return !!(g && typeof g.setCurrentInput === 'function');
    }

    function openGorgiasHandoff(handoff) {
      var closeBtn = document.querySelector('.advisor-drawer__close');
      if (closeBtn) closeBtn.click();
      var g = window.GorgiasChat;
      if (!g) return;
      try {
        if (typeof g.setCurrentInput === 'function') g.setCurrentInput(handoff.question);
      } catch (e) { /* prefill is best-effort by design */ }
      try { if (typeof g.open === 'function') g.open(); } catch (e) { /* widget owns its own state */ }
    }

    function handleReply(reply) {
      if (reply.handoff) {
        var line = reply.text || '';
        if (!gorgiasPrefillSupported() && reply.handoff.native_pointer) {
          line += ' ' + reply.handoff.native_pointer;
        }
        addMessage('advisor', line);
        // A beat to read the line, then hand over.
        setTimeout(function () { openGorgiasHandoff(reply.handoff); }, 1600);
        return;
      }
      if (reply.recommendation) {
        // The narration IS the system intro — it recombines the same approved
        // blocks, so it renders inside the card instead of doubling as a
        // bubble above a verbatim copy stack. An updated recommendation
        // (e.g. after the pillow invite) replaces only the results block —
        // the conversation above it stays scrollable.
        if (reply.text) reply.recommendation.narration = reply.text;
        renderRecommendation(reply.recommendation);
        return;
      }
      // The save-my-system block belongs to the results moment. When the
      // thread no longer shows a recommendation (start over, fresh session,
      // welcome-back), it leaves too and re-renders at the next results
      // (found case: it persisted through the whole chat after Start over).
      if (!thread.querySelector('.sleep-advisor__results')) {
        var staleOptin = root.querySelector('[data-advisor-optin]');
        if (staleOptin) staleOptin.remove();
      }
      var msgEl = null;
      if (reply.text) msgEl = addMessage('advisor', reply.text, undefined, reply.collection_link && reply.collection_link.url);
      // The message that carries chips is the question — heading-font
      // emphasis. When a separate gate ask follows (the break), the reveal
      // stays plain and the gate message takes the question treatment.
      if (msgEl && reply.chips && reply.chips.length && !reply.gate_text) msgEl.classList.add('sleep-advisor__msg--question');
      // v3.8.4 "the break is commercially complete": the stage-1 reveal
      // carries the primary pick addable in browse mode and the save offer,
      // before the gate is answered.
      if (reply.break_products && reply.break_products.length) {
        renderBreakCards(reply.break_products);
      }
      // v3.8.6: the gate ask renders after the card, directly above its
      // chips (found case: the tall card orphaned the chips from a question
      // buried at the end of the reveal paragraph).
      if (reply.gate_text) {
        var gateMsg = addMessage('advisor', reply.gate_text);
        gateMsg.classList.add('sleep-advisor__msg--question');
      }
      if (reply.show_optin && !localStorage.getItem(TOKEN_KEY)) {
        showOptin();
        // The always-available Start over stays LAST — below the save offer
        // (owner report: it sat above the save-my-system box at the break).
        root.appendChild(restartBtn);
      }
      renderChips(reply.chips);
      // Land each turn with the QUESTION at the top, not the bottom of its
      // options (owner report: photo questions auto-scrolled to mid-image,
      // and the break auto-scrolled past the reveal and card to the gate
      // chips). Scrolling to the first message of the turn shows the
      // question — or the reveal — with its content flowing below.
      if (msgEl) scrollToMessage(msgEl);
    }

    // Put a message at the top of the drawer's visible area. Synchronous
    // (layout is already computed when this runs) plus a rAF re-assert for
    // any late layout — rAF alone can be deferred (e.g. hidden tabs).
    function scrollToMessage(el) {
      function land() {
        scroller.scrollTop = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 12;
      }
      land();
      requestAnimationFrame(land);
    }

    // Browse-mode cards outside the results moment (the stage-1 break).
    function renderBreakCards(products) {
      var wrap = document.createElement('div');
      wrap.className = 'sleep-advisor__cards';
      thread.appendChild(wrap);
      products.forEach(function (p) {
        var el = document.createElement('div');
        el.className = 'sleep-advisor__card';
        wrap.appendChild(el);
        var card = { el: el, meta: p, product: null, sel: {}, colorConfirmed: false, colorIdx: -1 };
        fetch('/products/' + p.handle + '.js')
          .then(function (r) { if (!r.ok) throw new Error('' + r.status); return r.json(); })
          .then(function (product) {
            card.product = product;
            buildCard(card, null, null, null, true, { support: null });
            // No scroll: the reveal stays anchored at the top; the card
            // fills in below it (found case: this late scrollToEnd yanked
            // the break to the bottom once the product data arrived).
          })
          .catch(function () { el.remove(); });
      });
    }

    function renderRecommendation(rec, replay) {
      // The whole results moment lives in one container so an updated rec
      // (pillow invite) swaps it in place without touching the transcript.
      var oldResults = thread.querySelector('.sleep-advisor__results');
      var isUpdate = !!oldResults && !replay;
      if (oldResults) oldResults.remove();
      // An update that added a pillow (the invite flow) scrolls to the pillow
      // itself — landing back on the system top hid where the pillow went.
      scrollTargetRole = null;
      if (isUpdate) {
        ['flexcool', 'cloudsupport', 'fluffbase'].forEach(function (r) {
          if ((rec.rec_products || []).some(function (p) { return p.role === r; })) scrollTargetRole = r;
        });
      }
      resultsEl = document.createElement('div');
      resultsEl.className = 'sleep-advisor__results';
      thread.appendChild(resultsEl);

      var card = document.createElement('div');
      card.className = 'sleep-advisor__rec';

      var kicker = document.createElement('p');
      kicker.className = 'sleep-advisor__rec-kicker h6';
      // v3.8: a stage-1 exit is a fabric match (no shoppable cards yet); a
      // category session's hero is "your match", not a full system.
      kicker.textContent = rec.stage === 'fabric_match' ? 'Your fabric match'
        : rec.category ? 'Your match' : 'Your Sleep System';
      card.appendChild(kicker);

      var title = document.createElement('h3');
      title.className = 'sleep-advisor__rec-title';
      title.textContent = rec.system_name;
      card.appendChild(title);

      if (rec.pieces && rec.pieces.length) {
        var pieces = document.createElement('p');
        pieces.className = 'sleep-advisor__rec-pieces';
        pieces.textContent = rec.pieces.map(function (p) { return p.replace(/-/g, ' '); }).join(' · ');
        card.appendChild(pieces);
      }

      // One paragraph, not three: the narration covers the selling copy in
      // the customer's own terms; the verbatim block stack is the fallback
      // when no narration exists (structured mode, older saved sessions).
      // v3.8.3 stage-1 decline destination: "the whole [fabric] collection"
      // in the close line links to the fabric's collection page — everything
      // in it is the customer's match. DOM nodes only, never innerHTML.
      var link = rec.collection_link;
      var linkPhrase = link ? 'the whole ' + link.fabric + ' collection' : null;
      var linkRendered = false;
      (rec.narration ? [rec.narration] : rec.copy || []).forEach(function (paragraph) {
        var p = document.createElement('p');
        p.className = 'sleep-advisor__rec-copy';
        var idx = linkPhrase ? paragraph.indexOf(linkPhrase) : -1;
        if (idx !== -1) {
          p.appendChild(document.createTextNode(paragraph.slice(0, idx)));
          var a = document.createElement('a');
          a.href = link.url;
          a.textContent = linkPhrase;
          p.appendChild(a);
          p.appendChild(document.createTextNode(paragraph.slice(idx + linkPhrase.length)));
          linkRendered = true;
        } else {
          p.textContent = paragraph;
        }
        card.appendChild(p);
      });
      // The destination must exist even when the phrase isn't in the copy
      // (e.g. rehydration without the narration): a plain standalone link.
      if (link && !linkRendered) {
        var browseP = document.createElement('p');
        browseP.className = 'sleep-advisor__rec-copy';
        var browseA = document.createElement('a');
        browseA.href = link.url;
        browseA.textContent = 'Browse the ' + link.fabric + ' collection →';
        browseP.appendChild(browseA);
        card.appendChild(browseP);
      }

      // v3.8.7 (owner direction: per-card repetition was overkill): the
      // HSA/FSA sentence renders ONCE, here in the summary box, with the
      // how-it-works link. The server omits it for LuxeSoft recommendations,
      // where it rides the add-ons intro instead.
      if (rec.hsa_note) {
        var hsaP = document.createElement('p');
        hsaP.className = 'sleep-advisor__rec-copy';
        var hsaPhrase = "Here's how that works";
        var hsaIdx = rec.hsa_note.indexOf(hsaPhrase);
        if (hsaIdx !== -1) {
          hsaP.appendChild(document.createTextNode(rec.hsa_note.slice(0, hsaIdx)));
          var hsaA = document.createElement('a');
          hsaA.href = '/pages/hsa-fsa';
          hsaA.textContent = hsaPhrase;
          hsaP.appendChild(hsaA);
          hsaP.appendChild(document.createTextNode(rec.hsa_note.slice(hsaIdx + hsaPhrase.length)));
        } else {
          hsaP.textContent = rec.hsa_note;
        }
        card.appendChild(hsaP);
      }

      // v3.8 disc.note: one plain, factual note when a real discount is live.
      if (rec.discount_note) {
        var disc = document.createElement('p');
        disc.className = 'sleep-advisor__rec-copy';
        disc.textContent = rec.discount_note;
        card.appendChild(disc);
      }

      resultsEl.appendChild(card);
      renderRecCards(rec);

      // v3.8.5 decline re-entry (found gap: no path back into the fine-tune
      // after declining the gate): the fabric-match close offers one chip
      // reopening stage 2 exactly as if the gate was accepted — stage-1
      // answers intact, nothing re-asked.
      if (rec.stage === 'fabric_match') {
        var reRow = document.createElement('div');
        reRow.className = 'sleep-advisor__chips sleep-advisor__chips--inline';
        var reBtn = document.createElement('button');
        reBtn.type = 'button';
        reBtn.className = 'sleep-advisor__chip';
        reBtn.textContent = 'Dial it in after all';
        reBtn.addEventListener('click', function () {
          if (busy) return;
          reRow.remove();
          addMessage('customer', 'Dial it in after all');
          send({ chip: { field: '__stage_gate', value: 'yes' } });
        });
        reRow.appendChild(reBtn);
        resultsEl.appendChild(reRow);
      }

      if (!replay) {
        state.rec = rec;
        state.done = true;
        persist();
      }

      // Attribution: advisor_session_id rides the cart so order-created can join back.
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ attributes: { advisor_session_id: sessionId } })
      }).catch(function () { /* cart may not exist yet; attribute set again on next add */ });

      // v3.8.5: the fabric-match close keeps the composer open — a customer
      // can type their way back into stage 2 ("actually yes, let's do the
      // colors and sizes"), this visit or a later one.
      composer.style.display = rec.stage === 'fabric_match' ? '' : 'none';
      chipsEl.innerHTML = '';

      // v3.2: when the pillow module never opened, the results close with a
      // one-line, zero-pressure invitation; yes runs position + loft inline.
      if (rec.pillow_invite && rec.pillow_invite.text) {
        var invite = addMessage('advisor', rec.pillow_invite.text, false);
        invite.classList.add('sleep-advisor__msg--question');
        resultsEl.appendChild(invite);
        var inviteRow = document.createElement('div');
        inviteRow.className = 'sleep-advisor__chips sleep-advisor__chips--inline';
        [
          { field: '__pillow_invite', value: 'yes', label: 'Yes, match a pillow' },
          { field: '__pillow_invite', value: 'no', label: 'No thanks' }
        ].forEach(function (chip) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'sleep-advisor__chip';
          btn.textContent = chip.label;
          btn.addEventListener('click', function () {
            if (busy) return;
            inviteRow.remove();
            addMessage('customer', chip.label);
            var done = send({ chip: { field: chip.field, value: chip.value } });
            // One ask at a time: declining resolves this ask, so the deferred
            // rating (if this session is sampled) may follow the reply.
            if (chip.value === 'no' && done) done.then(renderCsatNow);
          });
          inviteRow.appendChild(btn);
        });
        resultsEl.appendChild(inviteRow);
      }

      // v3.8 category upgrade invite: one zero-pressure offer of the full
      // sleep setup after the hero card; declining ends cleanly.
      if (rec.upgrade_invite && rec.upgrade_invite.text) {
        var upgrade = addMessage('advisor', rec.upgrade_invite.text, false);
        upgrade.classList.add('sleep-advisor__msg--question');
        resultsEl.appendChild(upgrade);
        var upgradeRow = document.createElement('div');
        upgradeRow.className = 'sleep-advisor__chips sleep-advisor__chips--inline';
        [
          { field: '__upgrade', value: 'yes', label: 'Yes, build my full setup' },
          { field: '__upgrade', value: 'no', label: 'Just this for now' }
        ].forEach(function (chip) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'sleep-advisor__chip';
          btn.textContent = chip.label;
          btn.addEventListener('click', function () {
            if (busy) return;
            upgradeRow.remove();
            addMessage('customer', chip.label);
            var done = send({ chip: { field: chip.field, value: chip.value } });
            // One ask at a time: the rating may follow once the invite resolves.
            if (chip.value === 'no' && done) done.then(renderCsatNow);
          });
          upgradeRow.appendChild(btn);
        });
        resultsEl.appendChild(upgradeRow);
      }

      // csat.ask (revised July 22): a calibration sample, never shown at the
      // results reveal and never stacked with another ask. The rating is
      // stashed here and rendered only after the customer's final action —
      // an invite declined, or an add-to-cart (one ask at a time; found
      // case: the pillow invitation and the rating rendered stacked).
      pendingCsat = rec.csat && rec.csat.text ? rec.csat.text : null;

      if (!localStorage.getItem(TOKEN_KEY)) showOptin();

      // Keep the always-available Start over link last (below the opt-in and
      // the csat block, which inserts itself just above this).
      root.appendChild(restartBtn);

      // Land the results moment with the system name at the top, fully
      // visible — never past the recommendation (owner rule: long responses
      // land at their top, add-ons stay below the fold). Sync + rAF via
      // scrollToMessage; the pillow-invite update instead scrolls to the
      // delivered pillow card once it loads.
      if (!scrollTargetRole) scrollToMessage(card);
    }

    function money(cents) {
      var n = cents / 100;
      return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
    }

    // Theme-native add: bundle the cart-drawer sections into the request and
    // replay Impact's own cart:change event so its drawer opens and re-renders.
    function cartAdd(items) {
      var sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items, sections: sections.join(',') })
      }).then(function (r) {
        return r.json().then(function (json) {
          if (!r.ok) throw new Error(json.description || json.message || 'add failed');
          return fetch('/cart.js').then(function (cr) { return cr.json(); }).then(function (cart) {
            cart.sections = json.sections;
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart: cart }
            }));
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ attributes: { advisor_session_id: sessionId } })
            }).catch(function () {});
          });
        });
      });
    }

    function renderRecCards(rec) {
      var products = rec.rec_products || [];
      if (!products.length) return;

      var wantSize = SIZE_LABELS[(rec.variant_hints || {}).size_token] || null;
      var wantFill = (rec.variant_hints || {}).fill || null;
      var palette = rec.colorways || null;
      // v3.8.3 browse mode (stage-1 decline): stage 2 wasn't answered, so
      // size pickers start open — nothing preselected the customer didn't
      // choose. Flat-sheet and fill defaults stand (owner rules driven by
      // answered questions, not stage-2 ones); color is guarded as always.
      var browseMode = rec.stage === 'fabric_match';
      // v3.8.4: FluffBase Support follows the loft answer.
      var cardExtras = { support: (rec.variant_hints || {}).support || null };

      function spawnCards(list, container, registry) {
        list.forEach(function (p) {
          var el = document.createElement('div');
          el.className = 'sleep-advisor__card' + (p.attach ? ' sleep-advisor__card--attach' : '');
          container.appendChild(el);
          var card = { el: el, meta: p, product: null, sel: {}, colorConfirmed: false, colorIdx: -1, fillIdx: -1 };
          if (registry) registry.push(card);
          fetch('/products/' + p.handle + '.js')
            .then(function (r) { if (!r.ok) throw new Error('' + r.status); return r.json(); })
            .then(function (product) {
              card.product = product;
              buildCard(card, wantSize, wantFill, palette, browseMode, cardExtras);
              if (scrollTargetRole && card.meta.role === scrollTargetRole) {
                scrollTargetRole = null;
                requestAnimationFrame(function () {
                  scroller.scrollTop = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 12;
                });
              }
            })
            .catch(function () { el.remove(); });
        });
      }

      // The system: its cards, then one add-everything CTA.
      var systemCards = [];
      var wrap = document.createElement('div');
      wrap.className = 'sleep-advisor__cards';
      resultsEl.appendChild(wrap);
      spawnCards(products.filter(function (p) { return !p.attach; }), wrap, systemCards);

      var systemBtn = document.createElement('button');
      systemBtn.type = 'button';
      systemBtn.className = 'sleep-advisor__atc sleep-advisor__atc--system';
      systemBtn.textContent = 'Add the full system';
      var systemMsg = document.createElement('p');
      systemMsg.className = 'sleep-advisor__card-msg';
      // v3.8 category mode: a single hero card is not a "system" — its own
      // Add to bag carries the moment; complements add individually too.
      if (!rec.category) {
        wrap.appendChild(systemBtn);
        wrap.appendChild(systemMsg);
      }
      systemBtn.addEventListener('click', function () {
        var items = [];
        var skipped = 0;
        systemCards.forEach(function (card) {
          if (!card.product) return;
          var v = resolveVariant(card);
          if (v && v.available && (card.colorIdx === -1 || card.colorConfirmed)) items.push({ id: v.id, quantity: 1 });
          else skipped++;
        });
        if (!items.length) {
          systemMsg.textContent = 'Pick a color on each piece first, so nothing arrives in a shade you didn’t choose.';
          return;
        }
        systemMsg.textContent = skipped ? 'Adding the confirmed pieces. The rest just need a color.' : '';
        systemBtn.disabled = true;
        systemBtn.textContent = 'Adding…';
        cartAdd(items).then(function () {
          systemBtn.textContent = 'Added ✓';
          addMessage('advisor', 'Your system is in the bag. Everything ships together, and washing before first use is all the setup it needs.', false);
          renderCsatNow();
        }).catch(function () {
          systemBtn.disabled = false;
          systemBtn.textContent = 'Add the full system';
          systemMsg.textContent = 'That didn’t go through. Give it another try?';
        });
      });

      // Optional add-ons: clearly separate from the system — these add
      // individually and are never bundled into the full-system CTA.
      var attachProducts = products.filter(function (p) { return p.attach; });
      if (attachProducts.length) {
        var cross = document.createElement('div');
        cross.className = 'sleep-advisor__cross';
        var crossLabel = document.createElement('p');
        crossLabel.className = 'sleep-advisor__cross-label h6';
        crossLabel.textContent = 'Optional add-ons';
        cross.appendChild(crossLabel);
        (rec.attach_copy || []).forEach(function (paragraph) {
          var cp = document.createElement('p');
          cp.className = 'sleep-advisor__rec-copy';
          // cc.hsa (v3.5): "Here's how that works." links to the site's
          // HSA/FSA explainer. Built with DOM nodes, never innerHTML.
          var hsaPhrase = "Here's how that works";
          var idx = paragraph.indexOf(hsaPhrase);
          if (idx !== -1) {
            cp.appendChild(document.createTextNode(paragraph.slice(0, idx)));
            var a = document.createElement('a');
            a.href = '/pages/hsa-fsa';
            a.textContent = hsaPhrase;
            cp.appendChild(a);
            cp.appendChild(document.createTextNode(paragraph.slice(idx + hsaPhrase.length)));
          } else {
            cp.textContent = paragraph;
          }
          cross.appendChild(cp);
        });
        var crossCards = document.createElement('div');
        crossCards.className = 'sleep-advisor__cards';
        cross.appendChild(crossCards);
        resultsEl.appendChild(cross);
        spawnCards(attachProducts, crossCards, null);
      }
    }

    function resolveVariant(card) {
      var sel = card.sel;
      return (card.product.variants || []).find(function (v) {
        return Object.keys(sel).every(function (idx) {
          return v['option' + (Number(idx) + 1)] === sel[idx];
        });
      }) || null;
    }

    function buildCard(card, wantSize, wantFill, palette, browseMode, extras) {
      var el = card.el;
      var product = card.product;
      var options = product.options || [];

      var pdpUrl = '/products/' + card.meta.handle;
      // HSA/FSA is tag-driven (v3.2): the Shopify product tag is the source
      // of truth on any product, system items and add-ons alike.
      var tagHsa = (product.tags || []).some(function (t) { return /hsa/i.test(t); });
      if (product.featured_image) {
        var imgLink = document.createElement('a');
        imgLink.href = pdpUrl;
        imgLink.setAttribute('aria-label', product.title);
        var img = document.createElement('img');
        img.className = 'sleep-advisor__card-img';
        img.src = product.featured_image;
        img.alt = product.title;
        img.loading = 'lazy';
        imgLink.appendChild(img);
        el.appendChild(imgLink);
      }
      if (tagHsa || card.meta.hsa_fsa_eligible) {
        // Same shield-check badge as the listing pages (product-card__hsa-badge).
        var hsa = document.createElement('div');
        hsa.className = 'sleep-advisor__hsa';
        hsa.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M7 1L2 3v4c0 2.76 2.13 5.34 5 6 2.87-.66 5-3.24 5-6V3L7 1z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M4.5 7l2 2 3-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg> HSA\u2009/\u2009FSA Eligible';
        el.appendChild(hsa);
      }
      var body = document.createElement('div');
      body.className = 'sleep-advisor__card-body';
      el.appendChild(body);

      var title = document.createElement('p');
      title.className = 'sleep-advisor__card-title';
      var titleLink = document.createElement('a');
      titleLink.href = pdpUrl;
      titleLink.className = 'sleep-advisor__card-title-link';
      titleLink.textContent = product.title;
      title.appendChild(titleLink);
      body.appendChild(title);

      // v3.9.1 card-level sale pricing: when the Worker's verified automatic-
      // discount feed covers this product (card.meta.sale), the card shows the
      // sale price with the regular struck through, exactly as the site's
      // product cards do. Filled in by refresh() once a variant resolves.
      var priceRow = document.createElement('p');
      priceRow.className = 'sleep-advisor__card-price';
      priceRow.hidden = true;
      body.appendChild(priceRow);

      // Certification badges from the map (accuracy enforced server-side —
      // never a badge the product doesn't carry); each links to the
      // certificates page. Text chips until icon assets exist.
      // v4.1: the AiryWeight comforter's badges are LOFT-SCOPED (AAFA never on
      // All-Season, GRS never on Lightweight), so the row re-renders from
      // certs_by_fill whenever the fill selection changes.
      var certRow = null;
      var CERT_NAMES = { 'AAFA': 'Certified Asthma & Allergy Friendly', 'GOTS': 'GOTS certified organic', 'OEKO-TEX': 'OEKO-TEX Standard 100, Class 1 tested', 'FSC': 'FSC certified', 'GRS': 'Global Recycled Standard' };
      function renderCerts(list) {
        if (!certRow || !list) return;
        certRow.textContent = '';
        list.forEach(function (cert) {
          var a = document.createElement('a');
          a.href = '/pages/certificates-standards';
          a.className = 'sleep-advisor__cert h6';
          a.textContent = cert;
          a.title = CERT_NAMES[cert] || cert;
          certRow.appendChild(a);
        });
      }
      if ((card.meta.certs && card.meta.certs.length) || card.meta.certs_by_fill) {
        certRow = document.createElement('div');
        certRow.className = 'sleep-advisor__certs';
        body.appendChild(certRow);
        renderCerts(card.meta.certs || []);
      }

      if (card.meta.note) {
        var note = document.createElement('p');
        note.className = 'sleep-advisor__card-note';
        note.textContent = card.meta.note;
        body.appendChild(note);
      }
      if (card.meta.screen) {
        var screen = document.createElement('p');
        screen.className = 'sleep-advisor__card-note';
        screen.textContent = card.meta.screen;
        body.appendChild(screen);
      }

      var msg = document.createElement('p');
      msg.className = 'sleep-advisor__card-msg';

      var atc = document.createElement('button');
      atc.type = 'button';
      atc.className = 'sleep-advisor__atc';
      atc.textContent = 'Add to bag';

      function optionValues(idx) {
        var seen = [];
        (product.variants || []).forEach(function (v) {
          var val = v['option' + (idx + 1)];
          if (val && seen.indexOf(val) === -1) seen.push(val);
        });
        return seen;
      }

      var notifyWrap = document.createElement('div');
      notifyWrap.className = 'sleep-advisor__notify';
      notifyWrap.hidden = true;

      function showNotify(variant, colorName) {
        notifyWrap.hidden = false;
        if (notifyWrap.dataset.variant === String(variant.id)) return;
        notifyWrap.dataset.variant = String(variant.id);
        notifyWrap.innerHTML = '';
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sleep-advisor__notify-btn';
        btn.textContent = 'Notify me when ' + (colorName ? colorName + ' is' : 'it\u2019s') + ' back';
        notifyWrap.appendChild(btn);
        btn.addEventListener('click', function () {
          notifyWrap.innerHTML = '';
          var form = document.createElement('form');
          var em = document.createElement('input');
          em.type = 'email'; em.required = true; em.placeholder = 'Email address';
          em.setAttribute('aria-label', 'Email for back-in-stock alert');
          em.className = 'sleep-advisor__input';
          var go = document.createElement('button');
          go.type = 'submit'; go.className = 'sleep-advisor__notify-btn'; go.textContent = 'Notify me';
          form.appendChild(em); form.appendChild(go);
          notifyWrap.appendChild(form);
          form.addEventListener('submit', function (evt) {
            evt.preventDefault();
            // Klaviyo native back-in-stock (client API; Shopify catalog ids)
            fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + KLAVIYO_COMPANY_ID, {
              method: 'POST',
              headers: { 'content-type': 'application/json', revision: '2024-10-15' },
              body: JSON.stringify({
                data: {
                  type: 'back-in-stock-subscription',
                  attributes: {
                    channels: ['EMAIL'],
                    profile: { data: { type: 'profile', attributes: { email: em.value } } }
                  },
                  relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + notifyWrap.dataset.variant } } }
                }
              })
            }).then(function (r) {
              notifyWrap.innerHTML = '<p class="sleep-advisor__card-msg">' + (r.ok ? 'Done \u2014 we\u2019ll email you when it\u2019s back.' : 'That didn\u2019t go through \u2014 try again in a moment.') + '</p>';
            }).catch(function () {
              notifyWrap.innerHTML = '<p class="sleep-advisor__card-msg">That didn\u2019t go through \u2014 try again in a moment.</p>';
            });
          });
        });
      }

      function firstChoiceNotify() {
        // First-choice color sold out while a fallback is suggested: the
        // notify-me stays attached to the color they actually wanted.
        if (card.topPaletteColor && !card.paletteHasStock && card.availableForColor && !card.availableForColor(card.topPaletteColor)) {
          var wanted = (card.product.variants || []).find(function (pv) {
            if (pv['option' + (card.colorIdx + 1)] !== card.topPaletteColor) return false;
            return Object.keys(card.sel).every(function (k) {
              return Number(k) === card.colorIdx || pv['option' + (Number(k) + 1)] === card.sel[k];
            });
          });
          if (wanted) { showNotify(wanted, card.topPaletteColor); return; }
        }
        notifyWrap.hidden = true;
      }

      function refresh() {
        // v4.1 loft-scoped badges: recompute the cert row from the selected
        // fill value ("Lightweight" / "All-Season") when the card carries a
        // per-fill badge map.
        if (card.meta.certs_by_fill && card.fillIdx >= 0 && card.sel[card.fillIdx]) {
          var fillVal = card.sel[card.fillIdx];
          Object.keys(card.meta.certs_by_fill).forEach(function (k) {
            if (fillVal.indexOf(k) !== -1) renderCerts(card.meta.certs_by_fill[k]);
          });
        }
        var needsColor = card.colorIdx !== -1 && !card.colorConfirmed;
        // No color confirmed -> no resolvable variant yet; never show a
        // stock verdict for a color the customer hasn't picked.
        var v = needsColor ? null : resolveVariant(card);
        if (needsColor) {
          atc.disabled = true;
          atc.textContent = 'Add to bag';
          msg.textContent = 'Pick a color to add';
          firstChoiceNotify();
        } else if (!v) {
          atc.disabled = true;
          atc.textContent = 'Add to bag';
          // Browse mode leaves the size unchosen — say so instead of a
          // silently dead button.
          msg.textContent = Object.keys(card.sel).length < (card.product.options || []).length ? 'Pick a size to add' : '';
          notifyWrap.hidden = true;
        } else if (!v.available) {
          atc.disabled = true;
          atc.textContent = 'Sold out';
          msg.textContent = '';
          showNotify(v, card.colorIdx !== -1 ? card.sel[card.colorIdx] : null);
        } else {
          atc.disabled = false;
          var saleCents = null;
          var sale = card.meta.sale;
          if (sale && (sale.percent || sale.amount_cents)) {
            saleCents = sale.percent
              ? Math.round(v.price * (1 - sale.percent / 100))
              : Math.max(0, v.price - sale.amount_cents);
          }
          if (saleCents !== null && saleCents < v.price) {
            priceRow.hidden = false;
            priceRow.textContent = '';
            var saleEl = document.createElement('strong');
            saleEl.className = 'sleep-advisor__card-price-sale';
            saleEl.textContent = money(saleCents);
            var strike = document.createElement('s');
            strike.textContent = money(v.price);
            priceRow.appendChild(saleEl);
            priceRow.appendChild(document.createTextNode(' '));
            priceRow.appendChild(strike);
            // The button promises what checkout charges — the discounted price.
            atc.textContent = 'Add to bag · ' + money(saleCents);
          } else {
            priceRow.hidden = true;
            atc.textContent = 'Add to bag · ' + money(v.price);
          }
          msg.textContent = '';
          firstChoiceNotify();
        }
      }

      // Pre-pass: settle every non-color default (size, flat sheet, fill)
      // BEFORE the color ladder runs — color availability is judged against
      // the customer's actual size, which never flexes.
      options.forEach(function (opt, idx) {
        var name = opt && opt.name ? opt.name : String(opt);
        var values = optionValues(idx);
        if (/colou?r/i.test(name)) return;
        // v4.1: remember the fill/loft option so refresh() can re-render the
        // loft-scoped cert badges when the selection changes.
        if (/weight|fill/i.test(name)) card.fillIdx = idx;
        var isFlat = /flat/i.test(name) || values.some(function (val) { return /flat sheet/i.test(val); });
        if (values.length < 2) { card.sel[idx] = values[0]; return; }
        if (/size/i.test(name) && wantSize) {
          var match = values.find(function (val) { return sizeMatches(val, wantSize); });
          card.sel[idx] = match || values[0];
        } else if (/size/i.test(name) && browseMode) {
          // Browse mode: the size stays unchosen until the customer picks.
        } else if (isFlat) {
          card.sel[idx] = values.find(function (val) { return /^with\b/i.test(val); }) || values[0];
        } else if (/weight|fill/i.test(name) && wantFill) {
          card.sel[idx] = values.find(function (val) { return val.indexOf(wantFill) !== -1; }) || values[0];
        } else if (/support|firm/i.test(name) && extras && extras.support) {
          // v3.8.4: FluffBase Support follows the loft answer — a preselect
          // the customer sees and can change, like the comforter fill.
          // Live option values drift ("Soft"/"Medium" became "Soft"/"Firm",
          // Aug 3): firm and medium are the same bucket, so match either
          // before falling back to the first value.
          card.sel[idx] = values.find(function (val) { return val.indexOf(extras.support) !== -1; })
            || (/firm|medium/i.test(extras.support) ? values.find(function (val) { return /firm|medium/i.test(val); }) : null)
            || values[0];
        } else {
          card.sel[idx] = values[0];
        }
      });

      options.forEach(function (opt, idx) {
        // /products/<handle>.js gives options as {name, position, values}.
        var name = opt && opt.name ? opt.name : String(opt);
        var values = optionValues(idx);
        var isColor = /colou?r/i.test(name);
        var isSize = /size/i.test(name);
        // Sijo's flat-sheet option is literally named "Option" — detect by values.
        var isFlat = /flat/i.test(name) || values.some(function (val) { return /flat sheet/i.test(val); });
        if (values.length < 2 && !isColor) return;

        var group = document.createElement('div');
        group.className = 'sleep-advisor__optgroup';
        var groupLabel = document.createElement('span');
        groupLabel.className = 'sleep-advisor__optlabel h6';
        groupLabel.textContent = isColor ? 'Color' : isSize ? 'Size' : isFlat ? 'Flat sheet' : name;
        group.appendChild(groupLabel);

        var row = document.createElement('div');
        row.className = 'sleep-advisor__card-opts' + (isColor ? ' sleep-advisor__card-opts--swatches' : '');
        row.setAttribute('role', 'group');
        row.setAttribute('aria-label', groupLabel.textContent);
        group.appendChild(row);
        body.appendChild(group);

        if (isColor) {
          card.colorIdx = idx;
          // v3.1 availability-aware ladder. Size never flexes, fabric never
          // flexes; only color does, on three rungs: (1) the customer's
          // palette group by sales rank, (2) fresh neutrals as the universal
          // adjacent group, (3) everything else — shown, never preselected.
          var rank = function (list, c) {
            for (var i = 0; i < list.length; i++) { if (list[i].toLowerCase() === c.toLowerCase()) return i; }
            return -1;
          };
          var paletteList = palette ? palette.colors : [];
          var adjacentList = (palette && palette.adjacent) || [];
          var allValues = values.slice().sort(function (a, b) {
            var tier = function (c) {
              if (rank(paletteList, c) !== -1) return 0;
              if (rank(adjacentList, c) !== -1) return 1;
              return 2;
            };
            var ta = tier(a), tb = tier(b);
            if (ta !== tb) return ta - tb;
            var list = ta === 0 ? paletteList : ta === 1 ? adjacentList : null;
            return list ? rank(list, a) - rank(list, b) : 0;
          });
          var availableForColor = function (c) {
            return (product.variants || []).some(function (v) {
              if (v['option' + (idx + 1)] !== c || !v.available) return false;
              return Object.keys(card.sel).every(function (k) {
                return Number(k) === idx || v['option' + (Number(k) + 1)] === card.sel[k];
              });
            });
          };
          // Display filter (spec §6b): chips are FILTERED to the customer's
          // palette answer. The ladder's wider rungs appear only when the
          // palette rung can't serve: palette sold out in their size ->
          // add fresh neutrals; those dead too (or product has no palette
          // colors at all) -> full range, honestly noted below.
          var inPalette = allValues.filter(function (c) { return rank(paletteList, c) !== -1; });
          var inAdjacent = allValues.filter(function (c) { return rank(adjacentList, c) !== -1; });
          if (inPalette.length && inPalette.some(availableForColor)) {
            values = inPalette;
          } else if (inPalette.length && inAdjacent.some(availableForColor)) {
            values = inPalette.concat(inAdjacent);
          } else {
            values = allValues;
          }
          // Preselection stops at rung 2; beyond it the customer chooses
          // actively and the situation is named honestly.
          var suggested = null, rung = -1;
          for (var pi = 0; pi < paletteList.length && !suggested; pi++) {
            if (values.indexOf(paletteList[pi]) === -1) {
              for (var vj = 0; vj < values.length; vj++) { if (values[vj].toLowerCase() === paletteList[pi].toLowerCase()) { if (availableForColor(values[vj])) { suggested = values[vj]; rung = 1; } break; } }
            } else if (availableForColor(paletteList[pi])) { suggested = paletteList[pi]; rung = 1; }
          }
          if (!suggested) {
            for (var ai = 0; ai < adjacentList.length && !suggested; ai++) {
              for (var vk = 0; vk < values.length; vk++) { if (values[vk].toLowerCase() === adjacentList[ai].toLowerCase() && availableForColor(values[vk])) { suggested = values[vk]; rung = 2; } }
            }
          }
          card.suggestedColor = suggested;
          card.availableForColor = availableForColor;
          card.paletteHasStock = rung === 1;
          card.topPaletteColor = null;
          for (var ti = 0; ti < values.length; ti++) { if (rank(paletteList, values[ti]) !== -1) { card.topPaletteColor = values[ti]; break; } }
          if (palette && rung !== 1 && card.topPaletteColor) {
            var sizeKey = Object.keys(card.sel).find(function (k) { return Number(k) !== idx; });
            var sizeLabel = sizeKey !== undefined ? card.sel[sizeKey] : '';
            var groupWords = palette.group.replace(/_/g, ' and ');
            var ladderNote = document.createElement('p');
            ladderNote.className = 'sleep-advisor__card-msg';
            ladderNote.textContent = suggested
              ? 'Your ' + groupWords + ' tones are sold out' + (sizeLabel ? ' in ' + sizeLabel : '') + '; ' + suggested + '\u2019s available now.'
              : 'Your ' + groupWords + ' tones are sold out' + (sizeLabel ? ' in ' + sizeLabel : '') + ' \u2014 here\u2019s what\u2019s available.';
            group.appendChild(ladderNote);
          }
        }

        values.forEach(function (val) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'sleep-advisor__opt' + (isColor ? ' sleep-advisor__opt--swatch' : '');
          if (isColor) {
            var variant = (product.variants || []).find(function (v) { return v['option' + (idx + 1)] === val && v.featured_image; });
            if (variant) {
              var sw = document.createElement('img');
              sw.src = variant.featured_image.src + (variant.featured_image.src.indexOf('?') === -1 ? '?' : '&') + 'width=144';
              sw.alt = '';
              sw.loading = 'lazy';
              btn.appendChild(sw);
            }
            var lbl = document.createElement('span');
            lbl.textContent = val;
            btn.appendChild(lbl);
            if (card.suggestedColor && val.toLowerCase() === card.suggestedColor.toLowerCase()) btn.classList.add('is-suggested');
            if (card.availableForColor && !card.availableForColor(val)) {
              btn.classList.add('is-soldout');
              btn.setAttribute('aria-label', val + ' (sold out)');
            }
          } else {
            btn.textContent = val;
          }
          var selected = card.sel[idx] === val;
          if (selected) btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          btn.addEventListener('click', function () {
            card.sel[idx] = val;
            if (isColor) card.colorConfirmed = true;
            Array.prototype.forEach.call(row.children, function (c) {
              c.classList.remove('is-selected');
              c.setAttribute('aria-pressed', 'false');
            });
            btn.classList.add('is-selected');
            btn.setAttribute('aria-pressed', 'true');
            refresh();
          });
          row.appendChild(btn);
        });
      });

      var footer = document.createElement('div');
      footer.className = 'sleep-advisor__card-footer';
      footer.appendChild(atc);
      footer.appendChild(msg);
      footer.appendChild(notifyWrap);
      body.appendChild(footer);

      atc.addEventListener('click', function () {
        var v = resolveVariant(card);
        if (!v) return;
        atc.disabled = true;
        atc.textContent = 'Adding…';
        cartAdd([{ id: v.id, quantity: 1 }]).then(function () {
          atc.textContent = 'Added ✓';
          msg.textContent = '';
          // An add is the customer's final action — the deferred calibration
          // rating (if sampled) may appear now, never at the results reveal.
          renderCsatNow();
        }).catch(function () {
          atc.disabled = false;
          refresh();
          msg.textContent = 'That didn’t go through. Give it another try?';
        });
      });

      refresh();
    }

    function showOptin() {
      if (root.querySelector('[data-advisor-optin]')) return; // keep state across re-renders
      var node = optinTemplate.content.cloneNode(true);
      root.appendChild(node);
      var form = root.querySelector('[data-advisor-optin]');
      // TCPA reveal-on-check: disclosure + phone field appear at the moment
      // of consent; the phone is required only when SMS is ticked.
      var smsToggle = form.querySelector('[data-advisor-sms-toggle]');
      var smsDetail = form.querySelector('[data-advisor-sms-detail]');
      var phoneInput = smsDetail ? smsDetail.querySelector('[name="phone"]') : null;
      if (smsToggle && smsDetail) {
        smsToggle.addEventListener('change', function () {
          smsDetail.hidden = !smsToggle.checked;
          if (phoneInput) phoneInput.required = smsToggle.checked;
          if (smsToggle.checked && phoneInput) phoneInput.focus();
        });
      }
      form.addEventListener('submit', function (evt) {
        evt.preventDefault();
        var email = form.querySelector('[name="email"]').value;
        var emailOptIn = form.querySelector('[name="email_opt_in"]').checked;
        var smsOptIn = form.querySelector('[name="sms_opt_in"]').checked;
        if (!emailOptIn && !smsOptIn) return;
        api('/v1/session/' + sessionId + '/optin', {
          email: email,
          email_opt_in: emailOptIn,
          sms_opt_in: smsOptIn,
          phone: smsOptIn && phoneInput ? phoneInput.value : undefined,
          shopify_customer_id: root.dataset.customerId || undefined
        }).then(function (res) {
          if (res.returning_token) localStorage.setItem(TOKEN_KEY, res.returning_token);
          form.innerHTML = '<p class="sleep-advisor__optin-heading">Saved. Your system is on its way.</p>';
        }).catch(function () {
          form.querySelector('.sleep-advisor__optin-submit').textContent = 'Try again';
        });
      });
    }

    function send(payload) {
      busy = true;
      chipsEl.innerHTML = '';
      var typing = addTyping();
      return api('/v1/session/' + sessionId + '/message', payload)
        .then(function (reply) {
          typing.remove();
          handleReply(reply);
        })
        .catch(function (e) {
          typing.remove();
          if (isOutage(e)) { showOutage(); return; }
          addMessage('advisor', 'Hmm, that didn’t go through. Give it another try?', false);
          renderChips(state.chips, false);
        })
        .then(function () { busy = false; });
    }

    composer.addEventListener('submit', function (evt) {
      evt.preventDefault();
      var text = input.value.trim();
      if (!text || busy || !sessionId) return;
      input.value = '';
      addMessage('customer', text);
      send({ message: text });
    });

    // Start over is ALWAYS available, one tap (spec: start-over semantics) —
    // not only at the results moment. Found case: a customer parked
    // mid-conversation (e.g. at the stage gate) reopened the drawer into the
    // same replayed screen with no visible way out; the only escape was
    // TYPING "start over", which nobody would guess. One quiet persistent
    // link; a fresh session begins client-side (identified customers still
    // get the welcome-back confirm, where scratch stays one tap away).
    var restartBtn = document.createElement('button');
    restartBtn.type = 'button';
    restartBtn.className = 'sleep-advisor__restart';
    restartBtn.textContent = 'Start over';
    restartBtn.addEventListener('click', function () {
      if (busy) return;
      var sid = sessionId;
      state = { id: sid, msgs: [], chips: [], rec: null, done: false };
      pendingCsat = null;
      resultsEl = null;
      thread.innerHTML = '';
      chipsEl.innerHTML = '';
      composer.style.display = '';
      var optinBlock = root.querySelector('[data-advisor-optin]');
      if (optinBlock) optinBlock.remove();
      var csatBlock = root.querySelector('.sleep-advisor__csat');
      if (csatBlock) csatBlock.remove();
      // Scratch means scratch: the wipe goes THROUGH the server so the saved
      // answer profile actually clears (found case: a client-only restart
      // started a new session, the profile survived, and the state machine
      // dropped the customer straight back onto the same reveal — Start over
      // just repeated the screen). The session token survives per the
      // start-over semantics; a dead session falls back to a fresh one.
      if (sid) {
        busy = true;
        api('/v1/session/' + sid + '/message', { chip: { field: '__start_over', value: 'confirm' } })
          .then(function (reply) { handleReply(reply); persist(); })
          .catch(function () {
            clearState();
            sessionId = null;
            state = { id: null, msgs: [], chips: [], rec: null, done: false };
            startSession();
          })
          .then(function () { busy = false; });
      } else {
        clearState();
        startSession();
      }
    });
    root.appendChild(restartBtn);

    // Comforter-band entry (July 29): a topic chip on the collection page
    // queues the customer's own words in window.__islaPendingMessage; once
    // the session is live they are sent as a normal first message, so the
    // intent router steps into the comforter path with the signal already
    // mapped. Bounded retry while the session boots; the queue clears
    // synchronously on send, so it never fires twice even with multiple
    // advisor surfaces on the page.
    var entryTries = 0;
    function flushEntryMessage() {
      var text = window.__islaPendingMessage;
      if (!text) return;
      if (!sessionId || busy) {
        if (entryTries++ < 40) setTimeout(flushEntryMessage, 300);
        return;
      }
      window.__islaPendingMessage = null;
      entryTries = 0;
      addMessage('customer', text);
      send({ message: text });
    }
    document.addEventListener('advisor:entry-message', flushEntryMessage);

    // Boot. A visit-scoped session in sessionStorage resumes across pages and
    // surfaces; otherwise create a fresh session (returning customers resolve
    // via the signed token or Shopify login).
    var saved = loadState();
    var savedSid = null;
    try { savedSid = localStorage.getItem(SID_KEY); } catch (e) {}
    if ((!saved || !saved.id) && savedSid) {
      // v3.1: persistence is implemented, not assumed — local state is a
      // cache; the Worker is the source of truth. Rehydrate the result (or
      // the next open question) from the server before starting anything new.
      sessionId = savedSid;
      state.id = savedSid;
      fetch(API + '/v1/session/' + savedSid + '/result')
        .then(function (r) { if (!r.ok) throw new Error('' + r.status); return r.json(); })
        .then(function (res) {
          if (res.done) {
            if (res.text) res.recommendation.narration = res.text;
            renderRecommendation(res.recommendation);
          } else {
            api('/v1/session/' + savedSid + '/message', { resume: true }).then(function (reply) {
              // v3.9.1 (owner ruling, July 25): a resume that lands back at the
              // very beginning is not a resume. The drawer would present a
              // brand-new conversation on a stale server session, and any
              // leftover working state (found case: a category path from the
              // previous run) could hijack the new run. Fresh presentation =
              // fresh session, completely.
              var atOpener = (reply.chips || []).some(function (c) { return c.field === '__opener'; });
              if (atOpener) {
                try { localStorage.removeItem(SID_KEY); } catch (e) {}
                sessionId = null;
                state.id = null;
                startSession();
                return;
              }
              handleReply(reply);
            });
          }
        })
        .catch(function () {
          sessionId = null;
          state.id = null;
          startSession();
        });
      return;
    }
    if (saved && saved.id) {
      sessionId = saved.id;
      state = saved;
      saved.msgs.forEach(function (m) { addMessage(m.role, m.text, false, m.link); });
      if (saved.rec) {
        renderRecommendation(saved.rec, true);
      } else {
        // Mid-conversation resume: the trailing advisor message is the open question.
        if (saved.chips && saved.chips.length) {
          var advisorMsgs = thread.querySelectorAll('.sleep-advisor__msg--advisor');
          if (advisorMsgs.length) advisorMsgs[advisorMsgs.length - 1].classList.add('sleep-advisor__msg--question');
        }
        renderChips(saved.chips, false);
      }
      return;
    }

    function startSession() {
      api('/v1/session', {
        entry_point: entryPoint(),
        returning_token: localStorage.getItem(TOKEN_KEY) || undefined,
        shopify_customer_id: root.dataset.customerId || undefined
      }).then(function (reply) {
        sessionId = reply.session_id;
        handleReply(reply);
      }).catch(function (e) {
        if (isOutage(e)) { showOutage(); return; }
        addMessage('advisor', 'The advisor is taking a quick break. Please refresh in a moment.', false);
      });
    }
    startSession();
  }

  // Time-of-day theming (v3.8): the drawer dresses for the hour — Light by
  // day, Dusk within 45 minutes of sunrise/sunset, Night after dark — with
  // cross-fades (CSS transitions on the theme variables). Auto-selection uses
  // the visitor's local sun times ONLY when geolocation permission is already
  // granted for another reason; theming alone never triggers a permission
  // prompt. Otherwise an hour heuristic applies (06:00–18:00 light, else
  // night; dusk skipped). A manual override cycles Auto → Light → Dusk →
  // Night from the header toggle and persists in localStorage.
  function initIslaTheme() {
    var panel = document.querySelector('.advisor-drawer__panel');
    if (!panel || panel.dataset.islaThemeWired) return;
    panel.dataset.islaThemeWired = 'true';
    var KEY = 'advisor:theme';
    var coords = null;

    // Compact sunrise/sunset calculation (SunCalc-style). Null at poles.
    function sunTimes(date, lat, lng) {
      var rad = Math.PI / 180, dayMs = 86400000, J1970 = 2440588, J2000 = 2451545, J0 = 0.0009;
      var lw = rad * -lng, phi = rad * lat;
      var d = date.valueOf() / dayMs - 0.5 + J1970 - J2000;
      var n = Math.round(d - J0 - lw / (2 * Math.PI));
      var ds = J0 + lw / (2 * Math.PI) + n;
      var M = rad * (357.5291 + 0.98560028 * ds);
      var L = M + rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M)) + rad * 102.9372 + Math.PI;
      var dec = Math.asin(Math.sin(rad * 23.4397) * Math.sin(L));
      var Jnoon = J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
      var h0 = rad * -0.833;
      var w0 = Math.acos((Math.sin(h0) - Math.sin(phi) * Math.sin(dec)) / (Math.cos(phi) * Math.cos(dec)));
      if (isNaN(w0)) return null;
      var a = J0 + (w0 + lw) / (2 * Math.PI) + n;
      var Jset = J2000 + a + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
      var Jrise = Jnoon - (Jset - Jnoon);
      var fromJulian = function (j) { return new Date((j + 0.5 - J1970) * dayMs); };
      return { sunrise: fromJulian(Jrise), sunset: fromJulian(Jset) };
    }

    function autoTheme() {
      var now = new Date();
      if (coords) {
        var st = sunTimes(now, coords.lat, coords.lng);
        if (st) {
          var DUSK = 45 * 60000, t = now.getTime();
          var rise = st.sunrise.getTime(), set = st.sunset.getTime();
          if (Math.abs(t - set) <= DUSK || Math.abs(t - rise) <= DUSK) return 'dusk';
          return t > rise && t < set ? 'light' : 'night';
        }
      }
      var h = now.getHours();
      return h >= 6 && h < 18 ? 'light' : 'night';
    }

    function stored() {
      try { return localStorage.getItem(KEY) || 'auto'; } catch (e) { return 'auto'; }
    }

    function apply() {
      var mode = stored();
      var eff = mode === 'auto' ? autoTheme() : mode;
      panel.classList.remove('isla-theme-night', 'isla-theme-dusk', 'isla-theme-light');
      panel.classList.add('isla-theme-' + eff);
      var toggle = document.querySelector('[data-advisor-theme-toggle]');
      if (toggle) toggle.title = 'Theme: ' + mode + (mode === 'auto' ? ' (' + eff + ')' : '');
    }

    // Coordinates only when permission is ALREADY granted — never a prompt.
    if (navigator.permissions && navigator.permissions.query && navigator.geolocation) {
      navigator.permissions.query({ name: 'geolocation' }).then(function (status) {
        if (status.state === 'granted') {
          navigator.geolocation.getCurrentPosition(function (p) {
            coords = { lat: p.coords.latitude, lng: p.coords.longitude };
            apply();
          }, function () {}, { timeout: 8000, maximumAge: 3600000 });
        }
      }).catch(function () {});
    }

    var toggle = document.querySelector('[data-advisor-theme-toggle]');
    if (toggle) {
      var CYCLE = ['auto', 'light', 'dusk', 'night'];
      toggle.addEventListener('click', function () {
        var next = CYCLE[(CYCLE.indexOf(stored()) + 1) % CYCLE.length];
        try { localStorage.setItem(KEY, next); } catch (e) { /* private mode */ }
        apply();
      });
    }

    apply();
    // Re-evaluate as time passes so the cross-fade lands at the boundary.
    setInterval(apply, 5 * 60000);
  }

  function initDrawer() {
    var drawer = document.querySelector('[data-advisor-drawer]');
    if (!drawer || drawer.dataset.advisorDrawerWired) return;
    drawer.dataset.advisorDrawerWired = 'true';

    var lastTrigger = null;
    var triggers = document.querySelectorAll('[data-advisor-drawer-open]');

    function open() {
      drawer.hidden = false;
      document.documentElement.classList.add('advisor-drawer-open');
      triggers.forEach(function (btn) { btn.setAttribute('aria-expanded', 'true'); });
      // Lazy boot: the session is created only on first open.
      var root = drawer.querySelector('[data-sleep-advisor]');
      if (root) initAdvisor(root);
      var closeBtn = drawer.querySelector('.advisor-drawer__close');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      drawer.hidden = true;
      document.documentElement.classList.remove('advisor-drawer-open');
      triggers.forEach(function (btn) { btn.setAttribute('aria-expanded', 'false'); });
      if (lastTrigger) lastTrigger.focus();
    }

    triggers.forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.addEventListener('click', function (evt) {
        // Placement stamp (v4.1): only REAL clicks on a trigger claim it —
        // surfaces that open programmatically (entry cards, footer, flyout)
        // stamp their own placement first and trigger.click() must not
        // overwrite it (programmatic clicks arrive with isTrusted false).
        // Sections that own an Isla callout declare their own placement with
        // data-advisor-placement (see PLACEMENTS in the worker's admin UI);
        // without one a trigger is the header banner or the Isla band.
        if (evt.isTrusted) {
          window.__islaPlacement = btn.getAttribute('data-advisor-placement') ||
            (btn.closest('.isla-band') ? 'band' : 'banner');
          // Only the fabric comparison bars carry the fabric greeting frame;
          // a later click elsewhere must not inherit it.
          window.__islaEntrySource = null;
        }
        lastTrigger = btn; open();
      });
    });
    drawer.querySelectorAll('[data-advisor-drawer-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && !drawer.hidden) close();
    });

    // v3.2 owner feedback: the drawer NEVER opens itself — banner tap only
    // (Build Spec v1.7: never ambient). The conversation still persists and
    // restores fully whenever the customer chooses to reopen it.
  }

  // v3.1 §6b: on PDPs of products in the recommended system, badge the title,
  // highlight the palette-group swatches, and preselect the quiz size plus the
  // highest-ranked in-stock palette color. Everything stays user-changeable;
  // add-to-cart behaves natively. Non-system products render unchanged.
  function initPdpIntegration() {
    try {
      var m = location.pathname.match(/\/products\/([a-z0-9-]+)/);
      if (!m) return;
      var saved = loadState();
      if (!saved || !saved.rec || !saved.rec.rec_products) return;
      var rec = saved.rec;
      var entry = rec.rec_products.filter(function (p) { return p.handle === m[1]; })[0];
      if (!entry) return;

      // The theme renders the product-info block more than once (hidden
      // duplicates for sticky/quick views) — badge every instance so the
      // visible one always carries it.
      document.querySelectorAll('.product-meta__title-wrapper').forEach(function (titleWrap) {
        if (titleWrap.parentNode.querySelector('.sleep-advisor-pdp-badge')) return;
        var badge = document.createElement('div');
        badge.className = 'sleep-advisor-pdp-badge h6';
        // Naming pair (v3.7, built v3.9.1): "Pairs with" reads as a
        // recommendation; "In your sleep system" is reserved for products the
        // identified customer actually owns (owned_handles from order history).
        var ownedHere = (rec.owned_handles || []).indexOf(m[1]) !== -1;
        badge.textContent = ownedHere ? 'In your sleep system' : 'Pairs with your sleep system';
        titleWrap.parentNode.insertBefore(badge, titleWrap);
      });

      if (!document.querySelector('variant-picker')) return;
      fetch('/products/' + m[1] + '.js')
        .then(function (r) { if (!r.ok) throw new Error('' + r.status); return r.json(); })
        .then(function (product) {
          var opts = product.options || [];
          var colorPos = -1, sizePos = -1;
          opts.forEach(function (o, i) {
            var oname = o && o.name ? o.name : String(o);
            if (/colou?r/i.test(oname)) colorPos = i + 1;
            if (/size/i.test(oname)) sizePos = i + 1;
          });

          // The flat-sheet option's name is literally "Option" in product.js —
          // detect it by its values, like the rec cards do.
          var flatPos = -1, flatWith = null;
          opts.forEach(function (o, i) {
            var withVal = ((o && o.values) || []).filter(function (v) { return /with a flat sheet/i.test(v); })[0];
            if (withVal) { flatPos = i + 1; flatWith = withVal; }
          });

          var wantSize = SIZE_LABELS[(rec.variant_hints || {}).size_token] || null;
          var sizeName = null;
          if (sizePos !== -1 && wantSize) {
            (product.variants || []).some(function (v) {
              if (sizeMatches(v['option' + sizePos] || '', wantSize)) { sizeName = v['option' + sizePos]; return true; }
              return false;
            });
          }
          var available = function (colorName) {
            return (product.variants || []).some(function (v) {
              if (!v.available) return false;
              if ((v['option' + colorPos] || '').toLowerCase() !== colorName.toLowerCase()) return false;
              if (sizeName && sizePos !== -1 && v['option' + sizePos] !== sizeName) return false;
              return true;
            });
          };
          var wantColor = null;
          if (colorPos !== -1 && rec.colorways) {
            // Availability ladder: palette rank, then fresh neutrals; rung 3
            // (everything else) is an active choice, never preselected.
            var ladder = (rec.colorways.colors || []).concat(rec.colorways.adjacent || []);
            for (var i = 0; i < ladder.length; i++) {
              if (available(ladder[i])) { wantColor = ladder[i]; break; }
            }
          }
          // Moon markers mirror the rec card's ladder display: the customer's
          // palette colors where the product carries them, else the fresh-
          // neutral adjacents (found case: LuxeSoft has no warm colorways at
          // all, so a warm-and-earthy customer saw no markers and no legend
          // on a product the advisor itself recommended).
          var productColors = [];
          if (colorPos !== -1) {
            (product.variants || []).forEach(function (v) {
              var c = (v['option' + colorPos] || '').toLowerCase();
              if (c && productColors.indexOf(c) === -1) productColors.push(c);
            });
          }
          var inProduct = function (list) {
            return list.filter(function (c) { return productColors.indexOf(c) !== -1; });
          };
          var paletteColors = rec.colorways ? inProduct((rec.colorways.colors || []).map(function (c) { return c.toLowerCase(); })) : [];
          if (!paletteColors.length && rec.colorways) {
            paletteColors = inProduct((rec.colorways.adjacent || []).map(function (c) { return c.toLowerCase(); }));
          }

          function applyHighlights() {
            if (colorPos === -1 || !paletteColors.length) return;
            // Every instance — the theme renders duplicate product-info blocks.
            document.querySelectorAll('variant-picker').forEach(function (picker) {
              var hits = 0;
              var anyInput = null;
              picker.querySelectorAll('input[data-option-position="' + colorPos + '"]').forEach(function (input) {
                anyInput = anyInput || input;
                var l = picker.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(input.id) : input.id) + '"]');
                if (!l) return;
                var norm = l.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
                var sr = l.querySelector('.sr-only');
                var srText = sr ? sr.textContent.trim().toLowerCase() : null;
                var hit = paletteColors.some(function (pc) { return norm === pc || norm.indexOf(pc + ' ') === 0 || srText === pc; });
                if (hit) { l.classList.add('sleep-advisor-palette-match'); hits++; }
              });
              // Legend under the swatch row, once, only when markers exist
              if (hits && anyInput) {
                var fieldset = anyInput.closest('.variant-picker__option') || anyInput.closest('fieldset');
                if (fieldset && !fieldset.querySelector('.sleep-advisor-swatch-legend')) {
                  var legend = document.createElement('div');
                  legend.className = 'sleep-advisor-swatch-legend';
                  legend.textContent = 'Colors that match your system';
                  fieldset.appendChild(legend);
                }
              }
            });
          }
          // The picker re-renders from the server after EVERY variant change,
          // sometimes replacing ancestors of the picker itself — an observer
          // on any node inside that subtree gets detached with it. Watch the
          // body (never replaced) and re-apply SYNCHRONOUSLY: observer
          // callbacks run before the browser paints, so markers and legend
          // reappear in the same frame the theme swaps the DOM — no flash,
          // no layout jump (found case: every swatch click flashed the moons
          // and bounced everything below the legend). Re-applying is
          // idempotent; the guard stops our own legend append from recursing.
          var applying = false;
          new MutationObserver(function () {
            if (applying) return;
            applying = true;
            applyHighlights();
            applying = false;
          }).observe(document.body, { childList: true, subtree: true });
          applyHighlights();

          // Preselection settles in passes: one click per pass against a
          // fresh query (each click triggers a server re-render).
          var passes = 0;
          function settle() {
            if (passes++ > 6) return;
            var picker = document.querySelector('variant-picker');
            if (!picker) return;
            function controls(pos) {
              return Array.prototype.slice.call(picker.querySelectorAll('input[data-option-position="' + pos + '"]'));
            }
            function labelOf(input) {
              return picker.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(input.id) : input.id) + '"]');
            }
            // Labels vary by selector style (swatch sr-only text vs popover
            // rows that append availability text like "KingSold out") — match
            // on the sr-only value OR a normalized starts-with comparison.
            function matchesName(input, name) {
              var l = labelOf(input);
              if (!l || !name) return false;
              var n = name.toLowerCase();
              var norm = l.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
              if (norm === n || norm.indexOf(n + ' ') === 0 || norm.indexOf(n + 'sold') === 0) return true;
              var sr = l.querySelector('.sr-only');
              return !!sr && sr.textContent.trim().toLowerCase() === n;
            }
            controls(colorPos).forEach(function (input) {
              var lbl = labelOf(input);
              if (!lbl) return;
              var isMatch = paletteColors.some(function (pc) { return matchesName(input, pc); });
              if (isMatch) lbl.classList.add('sleep-advisor-palette-match');
            });
            var clicked = false;
            if (sizeName && sizePos !== -1) {
              var sizeInputs = controls(sizePos);
              var sizeChecked = sizeInputs.filter(function (i) { return i.checked; })[0];
              if (!sizeChecked || !matchesName(sizeChecked, sizeName)) {
                var target = sizeInputs.filter(function (i) { return matchesName(i, sizeName); })[0];
                if (target && !target.disabled) { target.click(); clicked = true; }
              }
            }
            if (!clicked && wantColor && colorPos !== -1) {
              var colorInputs = controls(colorPos);
              var colorChecked = colorInputs.filter(function (i) { return i.checked; })[0];
              if (!colorChecked || !matchesName(colorChecked, wantColor)) {
                var ct = colorInputs.filter(function (i) { return matchesName(i, wantColor); })[0];
                if (ct && !ct.disabled) { ct.click(); clicked = true; }
              }
            }
            // Flat-sheet default mirrors the rec cards (v3.1: sets default to
            // With a Flat Sheet; one tap to remove) — but only when a variant
            // actually carries it available in the quiz size.
            if (!clicked && flatWith) {
              var flatAvailable = (product.variants || []).some(function (v) {
                if (!v.available || v['option' + flatPos] !== flatWith) return false;
                if (sizeName && sizePos !== -1 && v['option' + sizePos] !== sizeName) return false;
                return true;
              });
              if (flatAvailable) {
                var flatInputs = controls(flatPos);
                var flatChecked = flatInputs.filter(function (i) { return i.checked; })[0];
                if (!flatChecked || !matchesName(flatChecked, flatWith)) {
                  var ft = flatInputs.filter(function (i) { return matchesName(i, flatWith); })[0];
                  if (ft && !ft.disabled) { ft.click(); clicked = true; }
                }
              }
            }
            setTimeout(settle, clicked ? 900 : 700);
          }
          settle();
        })
        .catch(function () { /* PDP integration is best-effort; native PDP wins */ });
    } catch (e) { /* never break the PDP */ }
  }

  // Collection/search/recommendation grids: any card whose product is in the
  // recommended system carries a compact "Your system" pill above its title —
  // the system follows the customer around the catalog, same moon mark as
  // the PDP badge and swatch markers. Content-area placement avoids the
  // image-overlay badge stack (sale / best-seller / award art).
  function initGridBadges() {
    try {
      var saved = loadState();
      if (!saved || !saved.rec || !saved.rec.rec_products) return;
      var handles = {};
      saved.rec.rec_products.forEach(function (p) { handles[p.handle] = true; });
      function apply() {
        document.querySelectorAll('.product-card').forEach(function (card) {
          if (card.querySelector('.sleep-advisor-grid-badge')) return;
          var link = card.querySelector('a[href*="/products/"]');
          if (!link) return;
          var m = (link.getAttribute('href') || '').match(/\/products\/([a-z0-9-]+)/);
          if (!m || !handles[m[1]]) return;
          // Image overlay, bottom-right, offset left of the quick-add slot
          // (owner feedback: no extra content line above the title).
          var figure = card.querySelector('.product-card__figure');
          if (!figure) return;
          var badge = document.createElement('div');
          badge.className = 'sleep-advisor-grid-badge h6';
          badge.textContent = 'Your system';
          figure.appendChild(badge);
        });
      }
      // Grids re-render on filtering/pagination — re-apply, debounced
      // (content-area insert, no flash risk like the variant picker).
      var timer = null;
      new MutationObserver(function () {
        if (timer) return;
        timer = setTimeout(function () { timer = null; apply(); }, 150);
      }).observe(document.body, { childList: true, subtree: true });
      apply();
    } catch (e) { /* never break the grid */ }
  }

  // Live-site trap (owner report, July 21): the theme preselects the first
  // available variant on load, so customers ordered 'No Flat Sheet' believing
  // they had a full set — the choice was never theirs. Until the customer
  // actively picks every non-color option, the dropdowns read 'Select size' /
  // 'Select option' and add-to-cart is disabled ('Select options to add').
  // Color keeps the theme's first-available preselect. The underlying inputs
  // stay checked — the gate is presentational plus a submit block — so the
  // theme's own variant machinery is never disturbed. Advisor customers whose
  // system includes this product skip the guard: their preselects are the
  // point of the integration.
  function initPdpOptionGuard() {
    try {
      var m = location.pathname.match(/\/products\/([a-z0-9-]+)/);
      if (!m) return;
      var saved = loadState();
      if (saved && saved.rec && (saved.rec.rec_products || []).some(function (p) { return p.handle === m[1]; })) return;
      if (!document.querySelector('variant-picker')) return;
      fetch('/products/' + m[1] + '.js')
        .then(function (r) { if (!r.ok) throw new Error('' + r.status); return r.json(); })
        .then(function (product) {
          var guarded = {}; // option position -> placeholder text
          (product.options || []).forEach(function (o, i) {
            var name = o && o.name ? String(o.name) : '';
            if (/colou?r/i.test(name)) return;
            if (((o && o.values) || []).length < 2) return;
            guarded[String(i + 1)] = 'Select ' + name.toLowerCase();
          });
          var keys = Object.keys(guarded);
          if (!keys.length) return;
          var atcLabel = keys.length === 1
            ? guarded[keys[0]].toLowerCase() + ' to add'
            : 'Select options to add';
          atcLabel = atcLabel.charAt(0).toUpperCase() + atcLabel.slice(1);
          var chosen = {};
          function pending() {
            return keys.filter(function (pos) { return !chosen[pos]; });
          }
          // Sticky-bar bridge (Aug 4 redesign): the bar mirrors the guard —
          // it reads pending() and re-syncs on every apply().
          window.__islaPdpGuard = { pending: pending, label: atcLabel };
          function apply() {
            var open = pending();
            // Non-destructive placeholder: the attribute hides the native
            // value and paints the placeholder via CSS ::before. Removing it
            // restores the native label instantly — required when the
            // customer picks the already-selected option, which triggers no
            // theme re-render to rebuild the span.
            document.querySelectorAll('variant-picker span[id$="-selected-value"]').forEach(function (span) {
              var pm = span.id.match(/-(\d+)-selected-value$/);
              if (!pm || !guarded[pm[1]]) return;
              if (open.indexOf(pm[1]) !== -1) {
                span.setAttribute('data-advisor-placeholder', guarded[pm[1]]);
              } else {
                span.removeAttribute('data-advisor-placeholder');
              }
            });
            document.querySelectorAll('form[action*="/cart/add"] button[type="submit"]').forEach(function (btn) {
              if (open.length) {
                if (!btn.hasAttribute('data-advisor-guard')) {
                  btn.setAttribute('data-advisor-guard', btn.textContent.replace(/\s+/g, ' ').trim());
                  btn.textContent = atcLabel;
                }
                btn.disabled = true;
              } else if (btn.hasAttribute('data-advisor-guard')) {
                btn.textContent = btn.getAttribute('data-advisor-guard');
                btn.removeAttribute('data-advisor-guard');
                btn.disabled = false;
              }
            });
            document.dispatchEvent(new CustomEvent('advisor:guard'));
          }
          // A real customer pick is a change event; theme re-renders never
          // fire one. Delegated + capture so it survives DOM replacement.
          document.addEventListener('change', function (evt) {
            var t = evt.target;
            if (t && t.matches && t.matches('input[data-option-position]')) {
              var pos = t.getAttribute('data-option-position');
              if (guarded[pos] && !chosen[pos]) { chosen[pos] = true; apply(); }
            }
          }, true);
          // Found case: tapping the already-selected FIRST option fires no
          // change event (the radio underneath is already checked), so the
          // dropdown kept reading 'Select option' and looked broken. A click
          // on a guarded position's label or input is a real pick either way.
          document.addEventListener('click', function (evt) {
            var t = evt.target;
            if (!t || !t.closest) return;
            var input = t.closest('input[data-option-position]');
            if (!input) {
              var label = t.closest('label[for]');
              if (label) {
                var el = document.getElementById(label.getAttribute('for'));
                if (el && el.matches && el.matches('input[data-option-position]')) input = el;
              }
            }
            if (!input) return;
            var pos = input.getAttribute('data-option-position');
            if (guarded[pos] && !chosen[pos]) { chosen[pos] = true; apply(); }
          }, true);
          // Belt for implicit form submission while options are unchosen.
          document.addEventListener('submit', function (evt) {
            var f = evt.target;
            if (f && f.matches && f.matches('form[action*="/cart/add"]') && pending().length) {
              evt.preventDefault();
            }
          }, true);
          // The theme re-renders the picker and buy button from the server on
          // every variant change — re-apply synchronously (pre-paint, no flash).
          var applying = false;
          new MutationObserver(function () {
            if (applying) return;
            applying = true;
            apply();
            applying = false;
          }).observe(document.body, { childList: true, subtree: true });
          apply();
        })
        .catch(function () { /* guard is best-effort; native PDP wins */ });
    } catch (e) { /* never break the PDP */ }
  }

  // "Meet Isla" on-page entry cards (design handoff, July 21): a card under
  // the PDP buy box ("Is eucalyptus your match?") and the green variant inside
  // the collection pages' "Find your fabric" block. Entry points for the
  // uninitiated — customers who already hold a recommendation see the
  // your-system marks instead, so the cards skip them. Both open the drawer
  // through the ribbon's own trigger; no drawer on the page, no cards.
  function initIslaEntryCards() {
    try {
      var trigger = document.querySelector('[data-advisor-drawer-open]');
      if (!trigger) return;
      // Cards show for everyone (found case: any saved rec hid them, so the
      // stakeholder testing the advisor could never see the placements). The
      // one exception: the PDP card stays off products already IN the
      // customer's system — that page carries the your-system treatment.
      var saved = loadState();
      var recHandles = {};
      if (saved && saved.rec && saved.rec.rec_products) {
        saved.rec.rec_products.forEach(function (p) { recHandles[p.handle] = true; });
      }
      var name = trigger.getAttribute('data-advisor-name') || 'Isla';

      // Isla's face: moon disc (blinks) + cutting disc + dot; colors come
      // from the container via currentColor/background-inherit (see CSS).
      function buildAvatar(className) {
        var avatar = document.createElement('span');
        avatar.className = 'isla-avatar ' + className;
        avatar.setAttribute('aria-hidden', 'true');
        ['isla-avatar__moon isla-blink', 'isla-avatar__cut', 'isla-avatar__dot'].forEach(function (c) {
          var part = document.createElement('span');
          part.className = c;
          avatar.appendChild(part);
        });
        return avatar;
      }

      function buildCard(variant, headline, sub, cta) {
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'isla-entry isla-entry--' + variant;
        card.setAttribute('aria-label', headline + ' ' + sub);
        var avatar = buildAvatar('isla-entry__avatar');
        var copy = document.createElement('span');
        copy.className = 'isla-entry__copy';
        var strong = document.createElement('strong');
        strong.textContent = headline;
        var small = document.createElement('small');
        small.textContent = sub;
        copy.appendChild(strong);
        copy.appendChild(small);
        var pill = document.createElement('span');
        pill.className = 'isla-entry__cta';
        pill.textContent = cta;
        card.appendChild(avatar);
        card.appendChild(copy);
        card.appendChild(pill);
        card.addEventListener('click', function () {
          // Fabric-context frame flag: only the comparison bars open with the
          // greeting's fabric line (Copy Blocks reconciliation, July 22).
          window.__islaEntrySource = variant === 'compare' ? 'compare' : null;
          window.__islaPlacement = { pdp: 'pdpcard', collection: 'collcard',
            compare: 'compare', sidebar: 'sidebar', certs: 'pagecard' }[variant] || variant;
          trigger.click();
        });
        return card;
      }

      var pm = location.pathname.match(/\/products\/([a-z0-9-]+)/);
      if (pm) {
        if (recHandles[pm[1]]) return;
        var fm = (document.title + ' ' + pm[1]).match(/eucalyptus|linen|bamboo|sateen|cotton/i);
        var headline = fm ? 'Is ' + fm[0].toLowerCase() + ' your match?' : 'Not sure it’s your match?';
        document.querySelectorAll('.product-info form[action*="/cart/add"]').forEach(function (form) {
          var block = form.closest('.product-info__block-item') || form;
          // Below the stock message (owner feedback): skip past the theme's
          // inventory block so the card never splits ATC from its stock line.
          var next = block.nextElementSibling;
          while (next && /inventory/i.test((next.className || '').toString())) {
            block = next;
            next = block.nextElementSibling;
          }
          if (next && next.classList && next.classList.contains('isla-entry--pdp')) return;
          block.insertAdjacentElement('afterend',
            buildCard('pdp', headline, 'Ask ' + name + '. Takes about a minute.', 'Ask ' + name));
        });
      }

      // Collection: slot into the existing "Find your fabric" block, matched
      // to the fabric tiles' width (owner feedback: not the full container).
      var headings = document.querySelectorAll('h1, h2, h3');
      for (var i = 0; i < headings.length; i++) {
        if (/find your fabric/i.test(headings[i].textContent)) {
          var host = headings[i].closest('.container') || headings[i].parentElement;
          if (host && !host.querySelector('.isla-entry--collection')) {
            var card = buildCard('collection', 'Can’t decide between four?',
              'Let ' + name + ' narrow it down for you.', 'Get matched →');
            host.appendChild(card);
            // Desktop centers fixed-width tiles inside a wider container —
            // size the card to the tile row's union; mobile scrolls the
            // tiles edge-to-edge, where CSS gutters take over instead.
            var sizeCard = function () {
              var items = host.querySelectorAll('.material-filter-item');
              if (!items.length) return;
              var fr = items[0].getBoundingClientRect();
              var lr = items[items.length - 1].getBoundingClientRect();
              var w = Math.round(lr.right - fr.left);
              if (w > 0) card.style.setProperty('--isla-collection-width', w + 'px');
            };
            sizeCard();
            window.addEventListener('resize', sizeCard);
          }
          break;
        }
      }

      // Certificates page (owner ask, Aug 3): cert questions are grounded in
      // the knowledge.bedding.certs fact set now, so the standards page gets
      // its own doorway — shoppers reading certifications are exactly the
      // values-driven customers Isla routes well.
      if (/^\/pages\/certificates-standards\/?$/.test(location.pathname)) {
        var certHost = document.querySelector('main') || document.body;
        if (certHost && !certHost.querySelector('.isla-entry--certs')) {
          var certCard = buildCard('certs', 'Questions about our certifications?',
            name + ' knows which products carry which standards, and what each one means.',
            'Ask ' + name + ' →');
          // This theme nests the footer section INSIDE <main> (owner report,
          // Aug 4: the card rendered below the footer) — insert above it.
          var certFooter = certHost.querySelector('.shopify-section--footer, .shopify-section-group-footer-group');
          if (certFooter && certFooter.parentNode) certFooter.parentNode.insertBefore(certCard, certFooter);
          else certHost.appendChild(certCard);
        }
      }

      // Fabric-comparison sections (multi-column): the Isla bar closes the
      // section — right where shoppers weigh options and stall. Covers "Pick
      // the Fabric That Suits You" (home/product/collections) and "What's
      // cooler than sleeping well?" (best-seller page) — same section type.
      for (var j = 0; j < headings.length; j++) {
        if (/pick the fabric|cooler than sleeping well/i.test(headings[j].textContent)) {
          var compareHost = headings[j].closest('.container') || headings[j].parentElement;
          if (compareHost && !compareHost.querySelector('.isla-entry--compare')) {
            compareHost.appendChild(buildCard('compare',
              'Still comparing? Let ' + name + ' decide for you.',
              'Answer a few quick questions and your Sleep Advisor matches you in under a minute.',
              'Get matched →'));
          }
          break;
        }
      }

      // Footer help card beside the newsletter (July 21 update): a
      // persistent, low-pressure way in from any page.
      var newsletterStack = document.querySelector('.footer__block--newsletter .v-stack');
      if (newsletterStack && !newsletterStack.querySelector('.isla-footer-card')) {
        var fc = document.createElement('button');
        fc.type = 'button';
        fc.className = 'isla-footer-card';
        fc.setAttribute('aria-label', 'Not sure what’s right for you? Ask ' + name + ', your Sleep Advisor.');
        fc.appendChild(buildAvatar(''));
        var fcopy = document.createElement('span');
        fcopy.className = 'isla-footer-card__copy';
        var fstrong = document.createElement('strong');
        fstrong.textContent = 'Not sure what’s right for you?';
        var fsmall = document.createElement('small');
        fsmall.textContent = 'Ask ' + name + ', your Sleep Advisor.';
        fcopy.appendChild(fstrong);
        fcopy.appendChild(fsmall);
        var fpill = document.createElement('span');
        fpill.className = 'isla-footer-card__cta';
        fpill.textContent = 'Ask ' + name + ' →';
        fc.appendChild(fcopy);
        fc.appendChild(fpill);
        fc.addEventListener('click', function () { window.__islaPlacement = 'footer'; trigger.click(); });
        newsletterStack.appendChild(fc);
      }

      // v3.8 placement 09 — collection filter sidebar (desktop): docked at
      // the bottom of the sticky filter rail, catching shoppers already
      // narrowing by hand. Daytime Light treatment by design; opens the same
      // drawer. The rail itself is hidden below the md breakpoint.
      var rail = document.querySelector('.facets-sidebar');
      if (rail && !rail.querySelector('.isla-entry--sidebar')) {
        var sidebarCard = buildCard('sidebar', 'Too many to choose from?',
          'Tell ' + name + ' how you sleep and she’ll narrow the results to your fabric.',
          'Ask ' + name + ' →');
        var sidebarAvatar = sidebarCard.querySelector('.isla-avatar');
        if (sidebarAvatar) sidebarAvatar.classList.add('isla-glow');
        rail.appendChild(sidebarCard);
      }

      // v3.9 placement 10 (v4 deck) — mobile filter flyout: Isla docks in the
      // sheet's sticky footer just above VIEW RESULTS, so scroll depth never
      // hides her however far the filter accordions run. Same "narrowing by
      // hand" moment as the desktop sidebar card, daytime Light treatment,
      // opens the same drawer. Below 380px CSS collapses the bar to an
      // "Ask Isla" pill inline with VIEW RESULTS.
      document.querySelectorAll('.facets-drawer [slot="footer"]').forEach(function (foot) {
        if (foot.querySelector('.isla-flyout-bar')) return;
        var fbar = document.createElement('button');
        fbar.type = 'button';
        fbar.className = 'isla-flyout-bar';
        fbar.setAttribute('aria-label', 'Ask ' + name + ', your Sleep Advisor');
        var fbAvatar = buildAvatar('isla-glow');
        fbar.appendChild(fbAvatar);
        var fbCopy = document.createElement('span');
        fbCopy.className = 'isla-flyout-bar__copy';
        var fbStrong = document.createElement('strong');
        fbStrong.textContent = 'Too many to choose from?';
        var fbSmall = document.createElement('small');
        fbSmall.textContent = 'Tell ' + name + ' how you sleep.';
        fbCopy.appendChild(fbStrong);
        fbCopy.appendChild(fbSmall);
        var fbCta = document.createElement('span');
        fbCta.className = 'isla-flyout-bar__cta';
        fbCta.textContent = 'Ask ' + name;
        fbar.appendChild(fbCopy);
        fbar.appendChild(fbCta);
        fbar.addEventListener('click', function () {
          // Close the filter sheet first so the advisor opens over the page.
          var sheet = fbar.closest('.facets-drawer');
          if (sheet) {
            if (typeof sheet.hide === 'function') sheet.hide();
            else sheet.removeAttribute('open');
          }
          window.__islaPlacement = 'filterbar';
          setTimeout(function () { trigger.click(); }, 150);
        });
        foot.insertBefore(fbar, foot.firstChild);
      });
    } catch (e) { /* entry cards are best-effort */ }
  }

  // Sticky add-to-cart bar redesign (owner handoff, Aug 4): the bar reports
  // the selection, the form makes it. Three states, mirroring the in-page
  // option guard: SELECT OPTIONS TO ADD (clickable — scrolls to the form and
  // rings it), NOTIFY ME WHEN AVAILABLE on a sold-out variant (Klaviyo BIS
  // inline, never a dead button), and a live ADD TO CART. Until options are
  // chosen the bar hides the variant line and shows the From price — the
  // backend default is never echoed back as the customer's choice.
  function initStickyBar() {
    try {
      var bar = document.querySelector('product-sticky-bar.product-sticky-bar');
      if (!bar) return;
      var m = location.pathname.match(/\/products\/([a-z0-9-]+)/);
      if (!m) return;
      var rerender = bar.closest('product-rerender') || bar.parentElement;
      var product = null;
      fetch('/products/' + m[1] + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { product = p; tick(); })
        .catch(function () { /* bar keeps theme behaviour */ });

      function guardPending() {
        return window.__islaPdpGuard ? window.__islaPdpGuard.pending() : [];
      }
      function currentVariant() {
        if (!product) return null;
        var idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
        var vid = idInput ? Number(idInput.value) : NaN;
        var match = null;
        (product.variants || []).forEach(function (v) { if (v.id === vid) match = v; });
        return match;
      }
      // Every write below is conditional (no-op when the DOM already matches)
      // — sync mutates inside its own observer's subtree, so unconditional
      // writes self-trigger an infinite observer loop (found case, Aug 4:
      // the PDP hung). Conditional writes make the loop converge in one pass;
      // the syncing flag is the belt to that suspender.
      var syncing = false;
      function setText(node, text) {
        if (node && node.textContent !== text) node.textContent = text;
      }
      function setAttr(node, name, on) {
        if (!node) return;
        if (on && !node.hasAttribute(name)) node.setAttribute(name, '');
        else if (!on && node.hasAttribute(name)) node.removeAttribute(name);
      }
      function sync() {
        var el = document.querySelector('product-sticky-bar.product-sticky-bar');
        if (!el) return;
        var btn = el.querySelector('button');
        if (!btn) return;
        syncing = true;
        try {
          var open = guardPending();
          var variant = currentVariant();
          setAttr(el, 'data-advisor-untouched', open.length > 0);
          var line = el.querySelector('.product-sticky-bar__variant');
          if (line && variant) setText(line, variant.title);
          // Mirror the in-page inventory line: cloned only when its text
          // actually changed (the snapshot guard), never on every pass.
          var srcInv = document.querySelector('.product-info__block-item .inventory');
          var barInv = el.querySelector('.product-sticky-bar__stock');
          var invText = srcInv ? srcInv.textContent.replace(/\s+/g, ' ').trim() : '';
          if (invText) {
            if (!barInv) {
              barInv = document.createElement('div');
              barInv.className = 'product-sticky-bar__stock';
              var stack = el.querySelector('.product-sticky-bar__info .v-stack');
              if (stack) stack.appendChild(barInv);
            }
            if (barInv && barInv.dataset.snapshot !== invText) {
              barInv.dataset.snapshot = invText;
              var clonedInv = srcInv.cloneNode(true);
              clonedInv.removeAttribute('id');
              clonedInv.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
              clonedInv.querySelectorAll('[class*="progress"]').forEach(function (n) { n.remove(); });
              barInv.innerHTML = '';
              barInv.appendChild(clonedInv);
            }
          } else if (barInv) {
            barInv.remove();
          }
          var label, mode;
          if (open.length) {
            label = (window.__islaPdpGuard && window.__islaPdpGuard.label) || 'Select options to add';
            mode = 'select';
          } else if (variant && !variant.available) {
            label = 'Notify me when available';
            mode = 'notify';
          } else {
            mode = 'live';
          }
          if (mode === 'live') {
            if (btn.hasAttribute('data-advisor-orig')) {
              setText(btn, btn.getAttribute('data-advisor-orig'));
              btn.removeAttribute('data-advisor-orig');
            }
            if (btn.disabled && btn.hasAttribute('data-advisor-enable')) btn.disabled = false;
            setAttr(btn, 'data-advisor-select', false);
            setAttr(btn, 'data-advisor-notify', false);
          } else {
            if (!btn.hasAttribute('data-advisor-orig')) {
              btn.setAttribute('data-advisor-orig', btn.textContent.replace(/\s+/g, ' ').trim());
            }
            setText(btn, label);
            if (btn.disabled) { btn.disabled = false; btn.setAttribute('data-advisor-enable', ''); }
            setAttr(btn, 'data-advisor-select', mode === 'select');
            setAttr(btn, 'data-advisor-notify', mode === 'notify');
          }
        } finally {
          setTimeout(function () { syncing = false; }, 0);
        }
      }
      function liftCheck() {
        var el = document.querySelector('product-sticky-bar.product-sticky-bar');
        var vis = !!(el && el.classList.contains('is-visible'));
        document.documentElement.classList.toggle('advisor-sticky-visible', vis);
        if (vis && el) {
          document.documentElement.style.setProperty('--advisor-sticky-h', (el.offsetHeight + 12) + 'px');
        }
      }
      function tick() { sync(); liftCheck(); }

      // Theme re-renders replace the bar's contents on every variant change;
      // re-apply synchronously (observers run before paint — the established
      // marker pattern, no flash).
      new MutationObserver(function () {
        if (syncing) return; // our own writes never re-enter
        tick();
      }).observe(rerender, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class'],
      });
      document.addEventListener('advisor:guard', tick);
      document.addEventListener('change', function (evt) {
        var t = evt.target;
        if (t && t.closest && t.closest('form[action*="/cart/add"], variant-picker')) setTimeout(tick, 0);
      }, true);
      // The bar's height differs per breakpoint (two rows on mobile) — the
      // bubble-lift offset must follow a resize, not stay stale.
      window.addEventListener('resize', function () { setTimeout(tick, 100); });

      document.addEventListener('click', function (evt) {
        var b = evt.target && evt.target.closest && evt.target.closest('.product-sticky-bar button');
        if (!b) return;
        if (b.hasAttribute('data-advisor-select')) {
          evt.preventDefault();
          evt.stopPropagation();
          var form = document.querySelector('.product-info') || document.querySelector('form[action*="/cart/add"]');
          if (form) {
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
            form.classList.remove('advisor-form-ring');
            void form.offsetWidth;
            form.classList.add('advisor-form-ring');
            setTimeout(function () { form.classList.remove('advisor-form-ring'); }, 1000);
          }
        } else if (b.hasAttribute('data-advisor-notify')) {
          evt.preventDefault();
          evt.stopPropagation();
          showBarBis();
        }
      }, true);

      function showBarBis() {
        var el = document.querySelector('product-sticky-bar.product-sticky-bar');
        var variant = currentVariant();
        if (!el || !variant) return;
        var existing = el.querySelector('.product-sticky-bar__bis');
        if (existing) {
          var input = existing.querySelector('input');
          if (input) input.focus();
          return;
        }
        var wrap = document.createElement('form');
        wrap.className = 'product-sticky-bar__bis';
        var em = document.createElement('input');
        em.type = 'email';
        em.required = true;
        em.placeholder = 'Email for a back-in-stock alert';
        em.setAttribute('aria-label', 'Email for back-in-stock alert');
        var go = document.createElement('button');
        go.type = 'submit';
        go.className = 'sleep-advisor__notify-btn';
        go.textContent = 'Notify me';
        wrap.appendChild(em);
        wrap.appendChild(go);
        el.appendChild(wrap);
        em.focus();
        wrap.addEventListener('submit', function (evt) {
          evt.preventDefault();
          fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + KLAVIYO_COMPANY_ID, {
            method: 'POST',
            headers: { 'content-type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: { data: { type: 'profile', attributes: { email: em.value } } }
                },
                relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variant.id } } }
              }
            })
          }).then(function (r) {
            wrap.innerHTML = '<p class="sleep-advisor__card-msg">' + (r.ok ? 'Done — we’ll email you when it’s back.' : 'That didn’t go through — try again in a moment.') + '</p>';
          }).catch(function () {
            wrap.innerHTML = '<p class="sleep-advisor__card-msg">That didn’t go through — try again in a moment.</p>';
          });
        });
      }

      tick();
    } catch (e) { /* the bar keeps theme behaviour on any failure */ }
  }

  // Mobile flyout nav card (rendered in header-sidebar.liquid): close the
  // sidebar, then open the advisor drawer. Delegated — the sidebar is a
  // custom element whose content mounts after us.
  function initFlyoutEntry() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target && evt.target.closest && evt.target.closest('[data-advisor-flyout-open]');
      if (!btn) return;
      var sidebar = document.getElementById('sidebar-menu');
      if (sidebar) {
        if (typeof sidebar.hide === 'function') sidebar.hide();
        else sidebar.removeAttribute('open');
      }
      var trigger = document.querySelector('[data-advisor-drawer-open]');
      window.__islaPlacement = 'flyout';
      if (trigger) setTimeout(function () { trigger.click(); }, 150);
    });
  }

  // Mobile footer accordion (design handoff, July 21): the theme's footer link
  // menus are always expanded; fold them into tap-to-open accordions on mobile.
  // The .isla-facc class is applied ONLY while the mobile media query matches,
  // so desktop and no-JS keep the menus fully expanded (SEO + a11y safe).
  function initFooterAccordion() {
    try {
      var stacks = document.querySelectorAll('.footer__block--links > .v-stack');
      if (!stacks.length) return;
      var mq = window.matchMedia('(max-width: 699px)');
      Array.prototype.forEach.call(stacks, function (stack) {
        var title = stack.querySelector('.h6');
        var list = stack.querySelector('ul');
        if (!title || !list) return;
        function sync() {
          if (mq.matches) {
            stack.classList.add('isla-facc');
            title.setAttribute('role', 'button');
            title.setAttribute('tabindex', '0');
            title.setAttribute('aria-expanded', stack.classList.contains('is-open') ? 'true' : 'false');
          } else {
            stack.classList.remove('isla-facc');
            title.removeAttribute('role');
            title.removeAttribute('tabindex');
            title.removeAttribute('aria-expanded');
          }
        }
        function toggle() {
          if (!mq.matches) return;
          var open = stack.classList.toggle('is-open');
          title.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        title.addEventListener('click', toggle);
        title.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
        });
        sync();
        if (mq.addEventListener) mq.addEventListener('change', sync);
        else if (mq.addListener) mq.addListener(sync);
      });
    } catch (e) { /* footer accordion is best-effort */ }
  }

  // The B-Corp cert logo lives inside the 3rd footer menu block and the theme
  // absolutely-positions it on mobile with no top, so once those menus fold
  // into accordions (initFooterAccordion) it floats over them. Move it below
  // the accordion on mobile (a static, centered, smaller mark); restore its
  // original spot on desktop. Breakpoint-aware, no resize thrash.
  function initFooterCert() {
    try {
      var cert = document.querySelector('.footer__certification');
      var blockList = document.querySelector('.footer__block-list');
      if (!cert || !blockList) return;
      var home = document.createComment('isla-cert-home');
      cert.parentNode.insertBefore(home, cert);
      var mq = window.matchMedia('(max-width: 699px)');
      function sync() {
        if (mq.matches) {
          if (cert.parentElement !== blockList) blockList.appendChild(cert);
          cert.classList.add('isla-cert-mobile');
        } else {
          if (home.parentNode) home.parentNode.insertBefore(cert, home.nextSibling);
          cert.classList.remove('isla-cert-mobile');
        }
      }
      sync();
      if (mq.addEventListener) mq.addEventListener('change', sync);
      else if (mq.addListener) mq.addListener(sync);
    } catch (e) { /* footer cert relocation is best-effort */ }
  }

  function initComforterPdpTrims() {
    // Interim until the comforter template suffix can flip post-BTS: the
    // sheets "Pick the Fabric" comparison still renders on comforter PDPs.
    try {
      var m = location.pathname.match(/\/products\/([a-z0-9-]+)/);
      if (!m) return;
      var handle = m[1];
      if (handle !== 'eucalyptus-comforter' && handle !== 'aerochill-cooling-comforter') return;
      document.querySelectorAll('.shopify-section').forEach(function (sec) {
        var h = sec.querySelector('h1, h2, h3, .title, [class*="heading"]');
        if (h && /pick\s+the\s+fabric/i.test(h.textContent || '')) {
          sec.style.display = 'none';
        }
      });
    } catch (e) { /* trim is best-effort */ }
  }

  function boot() {
    document.querySelectorAll('[data-sleep-advisor][data-autoboot]').forEach(initAdvisor);
    initComforterPdpTrims();
    initDrawer();
    initIslaTheme();
    initPdpIntegration();
    initGridBadges();
    initPdpOptionGuard();
    initStickyBar();
    initIslaEntryCards();
    initFlyoutEntry();
    initFooterAccordion();
    initFooterCert();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
